/**
 * DealScreenDetailView — Full Deal Screen results view (V2 field contract)
 * Authority: docs/agents/pitch-deck-review/DEAL_SCREEN_CARRY_CHAIN_CONTRACT.md
 *            docs/agents/pitch-deck-review/DEAL_SCREEN_FACT_GATE_SPEC.md
 * Config: DS_TYPOGRAPHY + DS_COLORS + KPI_TILE + SECTION_PANEL + DECISION_RATIONALE + ACTION_BUTTONS
 *         + VERDICT_PILL + CALL_BADGE + DS_DETAIL_VIEW from ui-invariants.js
 *         AGENT_CONFIG.deal_evaluator from task-display-config.js
 *
 * DS-CARD-V7 (WI-DS-RENDER-NAME-001):
 *   - Reading order: "What they do" context BEFORE verdict/decision (defect A)
 *   - Four-tile rationale: one-liner on every tile (defect C / CEO addition)
 *   - Bands-only display: no numeric scores to reader; band WORD always visible (defect D)
 *   - Band single-sourcing: resolveOverallScore reads DS_BAND_THRESHOLDS (defect E)
 *   - Name guard: filename-stem names render as neutral title (defect B)
 *   - Fact-gate appendix: collapsed, claimed/found/why/implication, dedup fix (defect F)
 *   - Chain legibility: readable pass/fail reason, no raw plumbing (defect I)
 * Zero hardcoded hex/rgb in style props. All values from token imports.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  VERDICT_PILL,
  CALL_BADGE,
  DS_DETAIL_VIEW as DV,
  DS_TYPOGRAPHY,
  DS_COLORS,
  KPI_TILE,
  SECTION_PANEL,
  DECISION_RATIONALE,
  ACTION_BUTTONS,
  RATIONALE_BLOCK,
  DEAL_SCREEN_CARD,
  DS_SPACING,
  DS_BORDERS,
  AI_REWRITING_STATE,
  BAND_WORD_ALWAYS_VISIBLE,
  DS_DECISION_LAYER,
  STYLE_GATE_NOTICE,
} from '@/config/ui-invariants';
import { DS_BAND_STRONG_MIN, DS_BAND_WEAK_MIN, internalBandToReaderLabel } from '@/config/ds-band-thresholds';
import { AccrezeoSheet } from '@/components/shared/AccrezeoSheet';
import SectionCalibrationFooter from '@/components/deal-screen/SectionCalibrationFooter';
import SectionProseEditor from '@/components/deal-screen/SectionProseEditor';
import CallOutcomeStrip from '@/components/deal-screen/CallOutcomeStrip';

/**
 * SpinIcon — replaces `animation: 'spin 1s linear infinite'` with Framer Motion.
 * Motion is the single animation runtime for this project per .impeccable.md;
 * the old inline `@keyframes spin` pattern is banned in components.
 */
function SpinIcon({ size = 16, color }) {
  return (
    <motion.span
      aria-hidden="true"
      style={{ display: 'inline-flex', lineHeight: 0 }}
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
    >
      <SpinnerGap size={size} color={color} />
    </motion.span>
  );
}

/**
 * PulseDot — replaces inline `@keyframes pulse` opacity loops on verdict dots.
 * Opacity-only animation per .impeccable.md (forbidden to animate layout props).
 */
function PulseDot({ size = 6, color }) {
  return (
    <motion.span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
      }}
      animate={{ opacity: [1, 0.5, 1] }}
      transition={{ repeat: Infinity, duration: 2, ease: [0.4, 0, 0.6, 1] }}
    />
  );
}
import { X as XIcon, FilePdf, Sparkle, PencilSimple, CaretDown, SpinnerGap } from '@phosphor-icons/react';
import { AGENT_CONFIG, INTENT_TO_BUTTON, SHARED_UI_LABELS } from '@/config/task-display-config';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';

// WI-DS-UI-003 T2: Action Registry compliance per DESIGN_LANGUAGE_SKILL.md Section 4
// AI Action = icon+label always. Danger = text visible always. Secondary = no icon.
const ACTION_ICON_MAP = {
  run_fca: Sparkle,              // AI Action — violet fill, always icon+label
  dismiss: XIcon,                // Danger — "Pass"
};
import { toast } from 'sonner';
import {
  getDealScreen,
  getDealScreenSectionHistory,
  archiveDealScreen,
  getDealScreenWorkItem,
  exportDealScreenPdf,
  runPreFCA,
  rerunDealScreenSection,
  updateStartupName,
  recordDealDecision,
  rerunDealScreen,
} from '@/lib/api';
import KnobsPanel from '@/components/deal-screen/KnobsPanel';
import useDealScreenStatus from '@/hooks/useDealScreenStatus';

const DEAL_CONFIG = AGENT_CONFIG.deal_evaluator || {};
const STAGE_LABELS = DEAL_CONFIG.stageLabels || {};
const CALL_DECISION_LABELS = DEAL_CONFIG.callDecisionLabels || {};
const SECTION_LABELS = DEAL_CONFIG.sectionLabels || {};
const SECTION_IDS = Object.keys(SECTION_LABELS);
const MARKET_LAYER_LABELS = DEAL_CONFIG.marketLayerLabels || {};
const CALL_STYLE = DEAL_CONFIG.callDecisionStyle || {};
const CEO_DECISION_DISPLAY = DEAL_CONFIG.ceoDecisionDisplay || {};
const ADJUST_BUTTON_CONFIG = DEAL_CONFIG.adjustAnalysisButton || {};
const ADJUST_PANEL_CONFIG = DEAL_CONFIG.adjustAnalysisSidePanel || {};

// Gate C decision layer config
const DECISION_LAYER_CONFIG = DEAL_CONFIG.decisionLayer || {};
const BAND_WORD_LABELS_CONFIG = DEAL_CONFIG.bandWordLabels || {};
const EVIDENCE_GRADE_LABELS = DEAL_CONFIG.evidenceGradeLabels || {};
const SOURCE_TAG_LABELS = DEAL_CONFIG.sourceTagLabels || {};

/**
 * resolveHeaderVerdict — WI-DEAL-SCREEN-VERDICT-DISPLAY-FIX-001.
 *
 * The Deal Screen header badge must show the CEO's authoritative call when one
 * has been recorded, NOT the agent's recommendation. These are two distinct
 * concepts that were previously conflated:
 *   - ceoDecision: most-recent entry of startups.decisions[] (value: 'active' |
 *     'passed'), written under CEO authority. This is the real decision.
 *   - agentCallDecision: startups.deal_screen.call_decision (e.g. 'not_worth_a_call'),
 *     written under agent authority. This is only a recommendation.
 *
 * Returns the source the header should render. CEO decision wins when present;
 * otherwise the agent recommendation is shown (unchanged legacy behaviour).
 * Pure + null-safe so it can be unit-tested in isolation.
 */
export function resolveHeaderVerdict(ceoDecision, agentCallDecision) {
  if (ceoDecision && ceoDecision.value) {
    return {
      source: 'ceo',
      value: ceoDecision.value,
      timestamp: ceoDecision.timestamp || null,
    };
  }
  return { source: 'agent', value: agentCallDecision || 'hold' };
}

/**
 * resolveOverallScore — Stream C remediation (DEAL-SCREEN-V2-CARD-001).
 *
 * Returns the persisted floor score, not a computed average. The original
 * `scoreMap` average was the symptom-4 bug: it ignored the persisted floor
 * and could read STRONG even when the lowest-scoring leg capped the screen.
 *
 * Band derivation reads DS_BAND_STRONG_MIN / DS_BAND_WEAK_MIN from
 * ds-band-thresholds.js (single source mirroring backend constants).
 * WI-DS-RENDER-NAME-001 defect E: NO inline literal thresholds here.
 *
 * Pure + null-safe so it can be unit-tested in isolation.
 */
export function resolveOverallScore(dealScreen) {
  if (!dealScreen || dealScreen.score == null) {
    return { score: null, floorLeg: null, band: 'NO_SIGNAL' };
  }
  const score = dealScreen.score;
  let band;
  if (score >= DS_BAND_STRONG_MIN) {
    band = 'STRONG';
  } else if (score >= DS_BAND_WEAK_MIN) {
    band = 'WEAK';
  } else {
    band = 'NO_SIGNAL';
  }
  return {
    score,
    floorLeg: dealScreen.score_floor_leg || null,
    band,
  };
}

/**
 * isFilenameStemName — WI-DS-RENDER-NAME-001 defect B name guard.
 *
 * Returns true when the stored startup name is a filename-stem placeholder.
 * Exact stem matching: compare the name (lowercased, spaces/underscores/hyphens
 * normalised) against the known filename-stem patterns loaded from the API or
 * inferred from the deal_screen document.
 *
 * PREFER exact stem match over broad "deck" substring checks — a company
 * legitimately named "DeckHand" must never be falsely flagged.
 *
 * filename_stem is the raw Path(filename).stem after title-casing. We compare
 * normalised strings so "Pitch Deck 2025" matches "pitch deck 2025".
 */
export function isFilenameStemName(name, filenameStem) {
  if (!name || !name.trim()) return false;
  const normalise = (s) => (s || '').trim().toLowerCase().replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ');
  // WI-F: a leaked name may still carry the deck file extension
  // (e.g. "truck parts pitch deck may 2026.pdf") while the persisted stem does not.
  // Strip a single trailing known-deck extension before comparing. A real company
  // name with a non-deck dotted suffix (e.g. "Acme.ai") is left intact.
  const DECK_EXTS = ['.pdf', '.pptx', '.ppt', '.key', '.docx', '.doc', '.txt', '.md'];
  const stripExt = (s) => {
    for (const ext of DECK_EXTS) {
      if (s.endsWith(ext)) return s.slice(0, -ext.length).trim();
    }
    return s;
  };
  const normName = stripExt(normalise(name));
  // Known generic placeholder stems that were never real company names
  const KNOWN_PLACEHOLDERS = new Set([
    'pasted text', 'name only', 'unknown', 'pitch deck', 'deck',
    'startup deck', 'investor deck', 'company deck',
  ]);
  if (KNOWN_PLACEHOLDERS.has(normName)) return true;
  // Exact stem match when stem is provided
  if (filenameStem) {
    const normStem = stripExt(normalise(filenameStem));
    if (normName === normStem) return true;
  }
  return false;
}

// ── Gate C: Pure derivation helpers (exported for jest tests) ────────────────

/**
 * resolveFloorContrarian — Gate C Row 2 (contrarian, non-collapsible).
 *
 * Maps score_floor_leg to the persisted section key. "market" is the legacy leg
 * name; the backend now persists the section as "market_signal". Both are handled.
 *
 * Returns { attack, severity } from the floor-leg's contrarian_challenge when
 * present; falls back to the lowest-band section's contrarian; null if none exist.
 */
export function resolveFloorContrarian(dealScreen) {
  if (!dealScreen) return null;

  // Map the floor-leg key. "market" -> "market_signal" (renamed in V2 persisted shape).
  const floorLeg = dealScreen.score_floor_leg;
  const FLOOR_KEY_MAP = { market: 'market_signal' };
  const sectionKey = FLOOR_KEY_MAP[floorLeg] || floorLeg;

  // Attempt to read the floor-leg section's contrarian.
  const floorSection = sectionKey ? dealScreen[sectionKey] : null;
  if (floorSection?.contrarian_challenge?.attack) {
    return floorSection.contrarian_challenge;
  }

  // Fallback: find the lowest-band section across ALL sections and take its contrarian.
  const SECTION_KEYS = ['problem', 'solution', 'market_signal', 'traction', 'market'];
  let lowestScore = Infinity;
  let lowestContrarian = null;

  for (const key of SECTION_KEYS) {
    const sec = dealScreen[key];
    if (!sec) continue;
    const score = sec.band_score ?? Infinity;
    if (sec.contrarian_challenge?.attack && score < lowestScore) {
      lowestScore = score;
      lowestContrarian = sec.contrarian_challenge;
    }
  }

  return lowestContrarian || null;
}

// Praise-pattern regex (Gate C Red Team Attack 2): applied only to WEAK / NO_SIGNAL bands.
const PRAISE_PATTERN = /\b(strong|excellent|impressive|proven|dominant)\b/i;
// Negating qualifiers that defuse a praise pattern.
const NEGATING_QUALIFIER = /\b(not|no|without|lack|missing|absent|unable|fails?|couldn't|doesn't|won't|isn't|aren't|wasn't|weren't)\b/i;

/**
 * resolveTileReason — Gate C Row 3 (per-tile one-liner).
 *
 * Returns section.band_reason if present; otherwise section.named_factors[0].finding.
 *
 * NET-POSITIVE-ON-WEAK GUARD: when section.band is WEAK or NO_SIGNAL and the
 * candidate text matches a praise pattern WITHOUT a negating qualifier nearby,
 * return null/"" instead of the praise. This prevents a confident-but-wrong
 * soundbite on a weak tile (Red Team Attack 2).
 */
export function resolveTileReason(section) {
  if (!section) return null;

  const weakBands = new Set(['WEAK', 'NO_SIGNAL']);
  const isWeak = weakBands.has(section.band);

  const candidate = section.band_reason
    || (section.named_factors && section.named_factors[0]?.finding)
    || null;

  if (!candidate) return null;

  if (isWeak && PRAISE_PATTERN.test(candidate) && !NEGATING_QUALIFIER.test(candidate)) {
    // Return empty string (not null) so .not.toContain() assertions operate on a string.
    return '';
  }

  return candidate;
}

/**
 * resolveOneQuestion — Gate C Row 4 (the one question for the call).
 *
 * Returns { question, source } from unresolved_questions[0]; source maps from
 * from_param (the raw API field name is never exposed as a key on the returned obj).
 *
 * Fallback: lowest-band section open_question ({ question, source: sectionKey }).
 * Returns null when neither path yields a question.
 */
export function resolveOneQuestion(dealScreen) {
  if (!dealScreen) return null;

  const unresolved = dealScreen.unresolved_questions;
  if (unresolved && unresolved.length > 0) {
    const first = unresolved[0];
    if (first.question) {
      return { question: first.question, source: first.from_param };
    }
  }

  // Fallback: lowest-band section by band_score.
  const SECTION_KEYS = ['problem', 'solution', 'market_signal', 'traction', 'market'];
  let lowestScore = Infinity;
  let result = null;

  for (const key of SECTION_KEYS) {
    const sec = dealScreen[key];
    if (!sec || !sec.open_question) continue;
    const score = sec.band_score ?? Infinity;
    if (score < lowestScore) {
      lowestScore = score;
      result = { question: sec.open_question, source: key };
    }
  }

  return result;
}

/**
 * isFilenameName — Gate C Row 1 (renderer-side filename guard).
 *
 * Returns true when startupName ends with a deck file extension (case-insensitive)
 * OR when startupName.toLowerCase() === filenameStem.toLowerCase() (when provided).
 * Returns false for real names, null, empty strings.
 */
export function isFilenameName(startupName, filenameStem) {
  if (!startupName || !startupName.trim()) return false;
  const DECK_EXT_RE = /\.(pdf|pptx?|docx?|key)$/i;
  if (DECK_EXT_RE.test(startupName.trim())) return true;
  if (filenameStem != null && startupName.toLowerCase() === filenameStem.toLowerCase()) return true;
  return false;
}

/**
 * resolveBandWord — Gate C Row 3 (tile heading band word).
 *
 * The ROW-3 tile band word MUST be the same on-enum reader vocabulary the
 * expanded section detail shows — {STRONG, WEAK} — NOT the friendlier
 * "Weak Signal"/"Strong Signal" phrasing. The CEO flagged the live Evamp
 * screen rendering "Weak Signal" in the tiles while the detail read "WEAK"
 * (Evamp output-quality council, B1). The reader-band contract enum is
 * ["STRONG","WEAK"] (deal_screen_output_contract.json reader_band_vocabulary_single;
 * NO_SIGNAL aliases to WEAK — the reader never sees NO_SIGNAL).
 *
 * Single source of truth: internalBandToReaderLabel() in ds-band-thresholds.js,
 * the same function the OverallScoreTile / SectionBandTile / expanded SectionPanel
 * use. resolveBandWord delegates so the tiles and the detail can never diverge again.
 */
export function resolveBandWord(band) {
  return internalBandToReaderLabel(band);
}

/**
 * sanitizeProse — Gate C render-boundary sanitizer.
 *
 * CONSERVATIVE: sentence-preserving. Removes only score/band machine artifacts
 * that should never reach a human reader. Does NOT delete whole clauses.
 *
 * Rules (in order):
 * 1. Lens / machine bracket tokens: [word:value] -> ""
 * 2. Parenthetical band tokens: (STRONG) / (WEAK) / (NO_SIGNAL) / (MODERATE) -> ""
 * 3. NN/100 forms: "72/100" -> ""
 * 4. "N-point" score refs: "72-point" -> ""
 * 5. Trailing score digit after a score/threshold word:
 *    "score at 52" -> "score"  — keeps the word, strips the number.
 *    Uses a narrow look-ahead so domain numbers (years, multiples) are untouched.
 * 6. Artifact cleanup: " ," / " ." / double spaces / leading+trailing whitespace.
 *
 * SAFE domain numbers that must NEVER be stripped:
 *   - "25-year"  (hyphenated modifier, not matched by step 4 because "year" follows)
 *   - "70%"      (percent sign breaks the \b boundary after digits)
 *   - "8–23×"    (en-dash range; "×" terminates the digit run)
 *   - "3 districts" (ordinary noun, not "score at 3")
 *
 * Null/undefined -> "" (safe to use in JSX expressions directly).
 */
export function sanitizeProse(text) {
  if (text == null) return '';
  let s = String(text);

  // 1. Lens / machine bracket tokens: [word_chars:anything-until-close-bracket]
  //    Includes the optional trailing whitespace so we don't leave a leading space.
  s = s.replace(/\[[\w][\w/:.\-]*:[^\]]*\]\s*/g, '');

  // 2. Parenthetical band tokens: (STRONG) / (WEAK) / (NO_SIGNAL) / (MODERATE)
  //    Case-insensitive. Leading whitespace consumed so "score 72 (STRONG)" -> "score 72".
  s = s.replace(/\s*\((?:STRONG|WEAK|NO_SIGNAL|MODERATE)\)/gi, '');

  // 3. NN/100 forms: "72/100", "52 / 100".
  s = s.replace(/\b\d{1,3}\s*\/\s*100\b/g, '');

  // 4. "N-point" score references ONLY when the suffix is "point" (not "year", "month", etc.).
  //    "72-point" -> ""   but "25-year" is not touched (different suffix).
  s = s.replace(/\b\d{1,3}-point\b/gi, '');

  // 5. Trailing bare score digit after a score/threshold keyword.
  //    Targets: "score at 52", "capped at 52", "cap the solution score at 52",
  //             "rated 72", "threshold 40".
  //    The replacement keeps the matched keyword (group 1) and any bridging text
  //    (group 2) but discards the numeral.
  //    Negative lookahead (?![\-–×%]|\s*year|\s*month|\s*district|\s*km|\s*mi)
  //    preserves "25-year", "70%", "8–23×", "3 districts".
  s = s.replace(
    /\b(score|scores|threshold|capped|cap|rated|rating)\b([^.\d]{0,20}?)\b(?:at|of|to|=)?\s*\d{1,3}\b(?![\-–×%]|\s*(?:year|month|district|km|mi|hour|day|week))/gi,
    '$1$2',
  );

  // 6. Artifact cleanup: " ," / " ." / " ;" / " :" artifacts, then collapse spaces.
  s = s.replace(/\s+([,.;:])/g, '$1');
  s = s.replace(/\s{2,}/g, ' ');
  s = s.trim();

  return s;
}

// ─────────────────────────────────────────────────────────────────────────────

const PILL = VERDICT_PILL.pill;
const BADGE_SHAPE = CALL_BADGE.badge;

// RT-12: VERDICT_LABEL removed — it was declared but never referenced, and its dead
// `moderate: 'Moderate'` entry was a band-word hazard (MODERATE retired by WI-R).

export function getVerdict(section) {
  if (!section) return 'unclear';
  // V2 band field takes priority (Hard-100 vocabulary: STRONG/WEAK/NO_SIGNAL)
  if (section.band) {
    if (section.band === 'STRONG') return 'strong';
    // WI-C: WEAK shows the word "Weak" (the 'weak' pill is amber — caution, not error),
    // NOT the kinder "Moderate". The displayed word must match the stored band + the prose.
    if (section.band === 'WEAK') return 'weak';
    return 'unclear'; // NO_SIGNAL or absent
  }
  if (section.provisional_verdict) return section.provisional_verdict;
  if (section.score != null) return section.score >= 7 ? 'strong' : section.score >= 4 ? 'moderate' : 'weak';
  return 'unclear';
}

// ── Primitives ────────────────────────────────────────────────────

function FieldLabel({ children, color }) {
  return (
    <span style={{
      display: 'block',
      ...DS_TYPOGRAPHY.monoLabel,
      color: color || DS_COLORS.text.muted,
      marginBottom: '5px',
    }}>{children}</span>
  );
}

function VerdictPillComponent({ verdict, size = 'sm' }) {
  const pill = VERDICT_PILL[verdict] || VERDICT_PILL.unclear;
  const isLg = size === 'lg';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      background: pill.background,
      color: pill.text,
      border: `${PILL.borderWidth} solid ${pill.border}`,
      borderRadius: PILL.borderRadius,
      padding: isLg ? '5px 13px' : `${PILL.paddingTop} ${PILL.paddingRight} ${PILL.paddingBottom} ${PILL.paddingLeft}`,
      fontFamily: DS_TYPOGRAPHY.monoLabel.fontFamily,
      ...DS_TYPOGRAPHY.verdictPillText,
      fontSize: isLg ? '12px' : PILL.fontSize,
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: isLg ? '7px' : pill.dotSize,
        height: isLg ? '7px' : pill.dotSize,
        borderRadius: '50%',
        background: pill.dot,
        flexShrink: 0,
      }} />
      {pill.label}
    </span>
  );
}

// RationaleBlock removed — it read q1_answer/q2_answer which are always null under V2.
// Replaced by NamedFactorsBlock inside SectionPanel (reads named_factors[]).
// KILL-LIST item 2: DEAL-SCREEN-V2-CARD-001.

// ── V2 helper components ──────────────────────────────────────────

/**
 * OverallScoreTile — bands-only overall signal tile (WI-DS-RENDER-NAME-001).
 * Reads the persisted floor score from resolveOverallScore(dealScreen).
 * KILL-LIST items 1 & 3: DEAL-SCREEN-V2-CARD-001.
 *
 * WI-1 change: shows BAND WORD only — no numeric score to the reader.
 * The band word (STRONG/WEAK — MODERATE retired by WI-R) is always present, bold ≥16px,
 * thick-bordered, strong contrast, ≥44px touch target.
 * band_score stays persisted (internal) but is NEVER rendered.
 */
function OverallScoreTile({ dealScreen }) {
  const { floorLeg, band } = resolveOverallScore(dealScreen);
  const readerLabel = internalBandToReaderLabel(band);
  // WI-C: WEAK uses the 'weak' colour key (amber, identical styling to the old
  // 'moderate' key) — no kinder relabel. The displayed word comes from internalBandToReaderLabel.
  const bandKey = band === 'STRONG' ? 'strong' : band === 'WEAK' ? 'weak' : 'unclear';
  const accentColor = KPI_TILE.accentBar.byVerdict[bandKey];
  const valueColor = BAND_WORD_ALWAYS_VISIBLE.bandColour[band] || KPI_TILE.valueColor[bandKey];
  const tileBg = KPI_TILE.tileBg[bandKey] ?? KPI_TILE.tileBg.default;
  const cappedBy = dealScreen?.director?.capped_by;
  const rationale = dealScreen?.opening_line || dealScreen?.deal_signal || null;

  return (
    <div style={{
      background: tileBg,
      border: `${BAND_WORD_ALWAYS_VISIBLE.minBorderWidthPx}px solid ${accentColor}`,
      borderRadius: KPI_TILE.shell.borderRadius,
      padding: KPI_TILE.shell.padding,
      position: 'relative',
      overflow: 'hidden',
      flex: 1,
      minWidth: 0,
      minHeight: `${BAND_WORD_ALWAYS_VISIBLE.minTouchTargetPx}px`,
    }}>
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: KPI_TILE.accentBar.height,
        borderRadius: KPI_TILE.accentBar.borderRadius,
        background: accentColor,
      }} />
      <FieldLabel>OVERALL SIGNAL</FieldLabel>
      {/* Band WORD — always visible, bold, ≥16px. NO numeric score rendered. */}
      <div style={{
        fontSize: `${Math.max(BAND_WORD_ALWAYS_VISIBLE.minFontSizePx, 20)}px`,
        fontWeight: BAND_WORD_ALWAYS_VISIBLE.minFontWeight,
        color: valueColor,
        marginBottom: '3px',
        letterSpacing: '0.5px',
      }}>
        {readerLabel}
      </div>
      {floorLeg && (
        <div style={{ ...DS_TYPOGRAPHY.monoSub, color: DS_COLORS.text.muted }}>
          {`floor: ${floorLeg}`}
          {cappedBy ? ` — capped by ${cappedBy}` : ''}
        </div>
      )}
      {/* One-line rationale — CEO LOCKED four-tile symmetry (WI-DS-RENDER-NAME-001 B) */}
      {rationale && (
        <div style={{
          ...DS_TYPOGRAPHY.monoSub,
          fontSize: '12px',
          color: DS_COLORS.text.secondary,
          marginTop: '6px',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>{rationale}</div>
      )}
    </div>
  );
}

/**
 * SectionBandTile — bands-only per-section tile (WI-DS-RENDER-NAME-001).
 * Reads band from the persisted V2 section object.
 * KILL-LIST item 3: DEAL-SCREEN-V2-CARD-001.
 *
 * WI-1 change: shows BAND WORD + one-line rationale. NO numeric band_score rendered.
 * Meets BAND_WORD_ALWAYS_VISIBLE invariant: bold, ≥16px, thick border, ≥44px tile.
 */
function SectionBandTile({ sectionId, section }) {
  const band = section?.band || 'NO_SIGNAL';
  const readerLabel = internalBandToReaderLabel(band);
  // WI-C: WEAK uses the 'weak' colour key (amber, identical styling to the old
  // 'moderate' key) — no kinder relabel. The displayed word comes from internalBandToReaderLabel.
  const bandKey = band === 'STRONG' ? 'strong' : band === 'WEAK' ? 'weak' : 'unclear';
  const accentColor = KPI_TILE.accentBar.byVerdict[bandKey];
  const valueColor = BAND_WORD_ALWAYS_VISIBLE.bandColour[band] || KPI_TILE.valueColor[bandKey];
  const tileBg = KPI_TILE.tileBg[bandKey] ?? KPI_TILE.tileBg.default;
  // One-line rationale: headline if present, else first named_factor finding, else null
  const rationale = section?.headline
    || (section?.named_factors && section.named_factors[0]?.finding)
    || null;

  return (
    <div style={{
      background: tileBg,
      border: `${BAND_WORD_ALWAYS_VISIBLE.minBorderWidthPx}px solid ${accentColor}`,
      borderRadius: KPI_TILE.shell.borderRadius,
      padding: KPI_TILE.shell.padding,
      position: 'relative',
      overflow: 'hidden',
      flex: 1,
      minWidth: 0,
      minHeight: `${BAND_WORD_ALWAYS_VISIBLE.minTouchTargetPx}px`,
    }}>
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: KPI_TILE.accentBar.height,
        borderRadius: KPI_TILE.accentBar.borderRadius,
        background: accentColor,
      }} />
      <FieldLabel>{SECTION_LABELS[sectionId] || sectionId}</FieldLabel>
      {/* Band WORD — always visible, bold, ≥16px. NO numeric score rendered. */}
      <div style={{
        fontSize: `${Math.max(BAND_WORD_ALWAYS_VISIBLE.minFontSizePx, 18)}px`,
        fontWeight: BAND_WORD_ALWAYS_VISIBLE.minFontWeight,
        color: valueColor,
        marginBottom: '3px',
        letterSpacing: '0.4px',
      }}>
        {readerLabel}
      </div>
      {/* One-line rationale — CEO LOCKED four-tile symmetry */}
      {rationale ? (
        <div style={{
          ...DS_TYPOGRAPHY.monoSub,
          fontSize: '12px',
          color: DS_COLORS.text.secondary,
          marginTop: '4px',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>{rationale}</div>
      ) : (
        <div style={{
          ...DS_TYPOGRAPHY.monoSub,
          fontSize: '12px',
          color: DS_COLORS.text.muted,
          marginTop: '4px',
          fontStyle: 'italic',
        }}>No signal from deck</div>
      )}
    </div>
  );
}

/**
 * NamedFactorsBlock — renders named_factors[] for a section (replaces q1/q2 RationaleBlock).
 * Each factor: factor name + evidence grade badge (config-labelled) + finding text.
 * Gate C: evidence_grade and source_tag now map to human-readable labels from config.
 * Raw underscores / ALL-CAPS tokens never rendered to the user.
 */
function NamedFactorsBlock({ factors }) {
  if (!factors || factors.length === 0) return null;
  const gradeColor = (grade) => {
    if (grade === 'revealed' || grade === 'verified' || grade === 'STRONG') return DS_COLORS.text.success;
    if (grade === 'stated' || grade === 'deck_claimed' || grade === 'WEAK') return DS_COLORS.signal.moderate;
    return DS_COLORS.text.muted;
  };
  const gradeLabel = (grade) => EVIDENCE_GRADE_LABELS[grade] || (
    grade ? grade.charAt(0).toUpperCase() + grade.slice(1).toLowerCase().replace(/_/g, ' ') : ''
  );
  const sourceLabel = (tag) => SOURCE_TAG_LABELS[tag] || (
    tag ? tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase().replace(/_/g, ' ') : ''
  );
  return (
    <div style={{ marginTop: `${DS_SPACING.labelToContent}px` }}>
      <span style={{
        display: 'block',
        ...DS_TYPOGRAPHY.monoLabel,
        color: DS_COLORS.text.muted,
        marginBottom: '8px',
      }}>KEY FACTORS</span>
      {factors.map((f, i) => (
        <div key={i} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          marginBottom: i < factors.length - 1 ? '10px' : 0,
          paddingLeft: '8px',
          borderLeft: `2px solid ${DS_COLORS.border.default}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{
              ...DS_TYPOGRAPHY.monoLabel,
              fontSize: '11px',
              color: DS_COLORS.text.primary,
              fontWeight: 600,
            }}>{f.factor}</span>
            {f.evidence_grade && (
              <span
                data-role="source-label"
                style={{
                  ...DS_TYPOGRAPHY.verdictPillText,
                  fontSize: '10px',
                  color: gradeColor(f.evidence_grade),
                  background: DS_COLORS.surface.slate,
                  borderRadius: DS_BORDERS.radiusPill,
                  padding: '2px 7px',
                  border: `1px solid ${DS_COLORS.border.default}`,
                }}>{gradeLabel(f.evidence_grade)}</span>
            )}
            {f.source_tag && (
              <span
                data-role="source-label"
                style={{
                  ...DS_TYPOGRAPHY.monoSub,
                  fontSize: '10px',
                  color: DS_COLORS.text.muted,
                }}>{sourceLabel(f.source_tag)}</span>
            )}
          </div>
          {f.finding && (
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              fontSize: '14px',
              color: DS_COLORS.text.body,
              margin: 0,
              lineHeight: 1.5,
            }}>{f.finding}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * StructuralEvidenceBlock — renders structural_evidence[] for a section.
 * Each item: claim + verified badge.
 */
function StructuralEvidenceBlock({ evidence }) {
  if (!evidence || evidence.length === 0) return null;
  return (
    <div style={{ marginTop: `${DS_SPACING.labelToContent}px` }}>
      <span style={{
        display: 'block',
        ...DS_TYPOGRAPHY.monoLabel,
        color: DS_COLORS.text.muted,
        marginBottom: '8px',
      }}>STRUCTURAL EVIDENCE</span>
      {evidence.map((item, i) => (
        <div key={i} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: i < evidence.length - 1 ? '6px' : 0,
        }}>
          <span style={{
            flexShrink: 0,
            marginTop: '2px',
            width: '16px',
            textAlign: 'center',
            color: item.verified ? DS_COLORS.text.success : DS_COLORS.text.muted,
            fontSize: '13px',
          }}>{item.verified ? '✓' : '○'}</span>
          <p style={{
            ...DS_TYPOGRAPHY.bodySm,
            fontSize: '14px',
            color: item.verified ? DS_COLORS.text.primary : DS_COLORS.text.secondary,
            margin: 0,
            lineHeight: 1.5,
          }}>{item.claim}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * CarriedQuestionBlock — renders a section's carry-chain RESOLUTION.
 * WI-D (cross-slot dedup): the carried question TEXT is intentionally not
 * re-printed here — it already appears once as the originating section's OPEN
 * QUESTION and once in the top-level carry-chain link. Re-printing it in the
 * answering section was a source of the same paragraph rendering 3-4× verbatim.
 * This block now shows only the status + the resolution (the answer).
 * A "Not answered" outcome is honest analysis, rendered in amber (not red).
 */
function CarriedQuestionBlock({ section }) {
  const { carried_question, carried_question_answered, carried_question_resolution } = section || {};
  // Nothing to show unless there is a resolution to report against the carried question.
  if (!carried_question || !carried_question_resolution) return null;

  const answered = carried_question_answered === true;
  const statusColor = answered ? DS_COLORS.text.success : DS_COLORS.signal.moderate;
  const statusLabel = answered ? 'Answered' : 'Not answered';

  return (
    <div style={{
      marginTop: `${DS_SPACING.labelToContent}px`,
      background: answered ? DS_COLORS.surface.white : DS_COLORS.surface.warm,
      border: `1px solid ${answered ? DS_COLORS.border.default : DS_COLORS.signal.moderate}`,
      borderRadius: DS_BORDERS.radiusCard,
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{
          ...DS_TYPOGRAPHY.monoLabel,
          color: DS_COLORS.text.muted,
        }}>CARRIED QUESTION</span>
        <span style={{
          ...DS_TYPOGRAPHY.verdictPillText,
          fontSize: '10px',
          color: statusColor,
          background: DS_COLORS.surface.slate,
          borderRadius: DS_BORDERS.radiusPill,
          padding: '2px 7px',
          border: `1px solid ${statusColor}`,
        }}>{statusLabel}</span>
      </div>
      <p style={{
        ...DS_TYPOGRAPHY.bodySm,
        fontSize: '13px',
        color: answered ? DS_COLORS.text.secondary : DS_COLORS.text.primary,
        margin: 0,
        lineHeight: 1.5,
      }}>{sanitizeProse(carried_question_resolution)}</p>
    </div>
  );
}

/**
 * OpenQuestionBlock — renders a section's open_question field.
 */
function OpenQuestionBlock({ openQuestion }) {
  if (!openQuestion) return null;
  return (
    <div style={{
      marginTop: `${DS_SPACING.labelToContent}px`,
      background: DS_COLORS.surface.slate,
      border: `1px solid ${DS_COLORS.border.default}`,
      borderRadius: DS_BORDERS.radiusCard,
      padding: '10px 14px',
    }}>
      <span style={{
        display: 'block',
        ...DS_TYPOGRAPHY.monoLabel,
        color: DS_COLORS.text.muted,
        marginBottom: '6px',
      }}>OPEN QUESTION</span>
      <p style={{
        ...DS_TYPOGRAPHY.bodySm,
        fontSize: '14px',
        color: DS_COLORS.text.secondary,
        margin: 0,
        fontStyle: 'italic',
        lineHeight: 1.5,
      }}>{openQuestion}</p>
    </div>
  );
}

/**
 * CarryChainStatusBlock — top-level carry-chain status card (WI-DS-RENDER-NAME-001).
 * WI-1 legibility upgrade (defect I):
 *   - Chain label shows plain English "Gap detected" / "Chain intact"
 *   - Each link: one readable question + a plain pass/fail reason sentence
 *   - No raw plumbing: no literal "|" pipes, no PASS/FAIL enums, no "UNANSWERED"
 *   - FAIL is amber (analytical finding, not a pipeline error)
 */
function CarryChainStatusBlock({ carryChain }) {
  if (!carryChain) return null;
  const { chain_status, problem_to_solution, problem_solution_to_market } = carryChain;
  const isFail = chain_status === 'fail';
  const statusColor = isFail ? DS_COLORS.signal.moderate : DS_COLORS.text.success;
  const borderColor = isFail ? DS_COLORS.signal.moderate : DS_COLORS.border.default;
  const bg = isFail ? DS_COLORS.surface.warm : DS_COLORS.surface.white;

  /**
   * Sanitise a raw chain question — strip literal pipes and multi-question welds.
   * Returns only the first clean question sentence.
   */
  const sanitiseQuestion = (raw) => {
    if (!raw) return null;
    // Strip pipe-separated question welds — keep only the first question
    const first = raw.split(/\s*\|\s*/)[0].trim();
    // Remove common machine-token prefixes
    return first.replace(/^(QUESTION:|Q:|question:)\s*/i, '').trim() || null;
  };

  /**
   * Derive a plain-English reason sentence from the link's answered_by / resolution fields.
   * Never surfaces raw enum values or "UNANSWERED_*" prefixes.
   */
  const deriveReason = (link) => {
    if (!link) return null;
    const answeredBy = link.answered_by || '';
    if (link.status === 'pass') {
      if (answeredBy && !answeredBy.startsWith('UNANSWERED')) {
        return answeredBy;
      }
      return 'This question is addressed by the next section.';
    }
    // fail
    if (answeredBy.startsWith('UNANSWERED')) {
      return 'This question is not resolved — the gap carries forward.';
    }
    return answeredBy || 'This question remains open.';
  };

  const LinkRow = ({ label, link }) => {
    if (!link) return null;
    const linkFail = link.status === 'fail';
    const question = sanitiseQuestion(link.question);
    const reason = deriveReason(link);
    const linkStatusLabel = linkFail ? 'Gap' : 'Resolved';
    return (
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        marginBottom: '10px',
        paddingBottom: '10px',
        borderBottom: `1px solid ${DS_COLORS.border.default}`,
      }}>
        <span style={{
          flexShrink: 0,
          marginTop: '2px',
          color: linkFail ? DS_COLORS.signal.moderate : DS_COLORS.text.success,
          fontSize: '14px',
        }}>{linkFail ? '⚠' : '✓'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {label && (
            <span style={{
              display: 'block',
              ...DS_TYPOGRAPHY.monoLabel,
              fontSize: '11px',
              color: DS_COLORS.text.muted,
              marginBottom: '4px',
            }}>{label}</span>
          )}
          {question && (
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              fontSize: '14px',
              color: DS_COLORS.text.primary,
              margin: '0 0 4px 0',
              fontWeight: 500,
              lineHeight: 1.5,
            }}>{question}</p>
          )}
          {reason && (
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              fontSize: '13px',
              color: linkFail ? DS_COLORS.signal.moderate : DS_COLORS.text.secondary,
              margin: 0,
              lineHeight: 1.4,
            }}>{reason}</p>
          )}
        </div>
        <span style={{
          ...DS_TYPOGRAPHY.verdictPillText,
          fontSize: '11px',
          color: linkFail ? DS_COLORS.signal.moderate : DS_COLORS.text.success,
          background: DS_COLORS.surface.slate,
          borderRadius: DS_BORDERS.radiusPill,
          padding: '3px 9px',
          border: `1px solid ${linkFail ? DS_COLORS.signal.moderate : DS_COLORS.border.default}`,
          flexShrink: 0,
          fontWeight: 600,
        }}>{linkStatusLabel}</span>
      </div>
    );
  };

  return (
    <div style={{
      background: bg,
      border: `1px solid ${borderColor}`,
      borderRadius: DS_BORDERS.radiusCard,
      padding: '14px 18px',
      marginBottom: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <span style={{
          ...DS_TYPOGRAPHY.sectionTitle,
          color: DS_COLORS.text.primary,
        }}>Argument Chain</span>
        <span style={{
          ...DS_TYPOGRAPHY.verdictPillText,
          fontSize: '11px',
          color: statusColor,
          background: isFail ? DS_COLORS.surface.warm : DS_COLORS.surface.white,
          borderRadius: DS_BORDERS.radiusPill,
          padding: '3px 10px',
          border: `1px solid ${borderColor}`,
          fontWeight: 700,
        }}>{isFail ? 'Gap detected — argument does not connect end-to-end' : 'Chain intact'}</span>
      </div>
      <LinkRow label={DEAL_CONFIG.carryChainLabels?.problem_to_solution || 'Problem → Solution'} link={problem_to_solution} />
      <LinkRow label={DEAL_CONFIG.carryChainLabels?.problem_solution_to_market || 'Solution → Market'} link={problem_solution_to_market} />
    </div>
  );
}

/**
 * FactGateBlock — collapsed appendix (WI-DS-RENDER-NAME-001 defect F).
 *
 * WI-1 changes:
 *   - Renders as a collapsed appendix (expand on click)
 *   - Fixes duplicated-token bug: when verified_value equals deck_value after
 *     normalisation, do NOT render deck→verified diff ("9 9", "5.82% 5.82%")
 *   - Each row: claimed / found / why / implication where data allows
 *   - Only shown when fact_gate.action !== 'allow'
 */
/**
 * Sector state line (sector tagging row R1, 3 Oct 2026).
 *
 * Says what the company's STARTUP RECORD holds for its sector, in the CEO-ruled four words: OFFICIAL (one
 * code with its basis), PROVISIONAL, NOT DETERMINED (the run has no final code), NOT CHECKED (this screen did
 * not read the record: never "no record exists"). The wording is the backend reader's own
 * (`sector_door.resolve_sector_state`); this component renders it and the canonical company id, and adds
 * nothing. It is the only place this view says the sector: the screen's own detected sector is not printed
 * (ruling 241, Law 8.4), so a screen that disagrees with the record cannot read as the record's sector.
 */
export function SectorStateLine({ state }) {
  // No state from the backend (an older response) is NOT CHECKED: this surface did not read the record. It
  // is never rendered as silence, and never as "no record exists".
  const notChecked = DEAL_CONFIG.sectorStateNotCheckedLabel;
  const shown = state && state.label
    ? state
    : (notChecked ? { state: 'NOT CHECKED', label: notChecked, canonical_id: null } : null);
  if (!shown) return null;
  state = shown;
  const coordinate = state.sector_l1 && state.sector_l2 ? `${state.sector_l1} \u00B7 ${state.sector_l2}` : null;
  const levelsMissing = state.state === 'OFFICIAL' && !coordinate;
  return (
    <div
      data-testid="ds-sector-state"
      data-sector-state={state.state}
      style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '8px' }}
    >
      <span style={{ ...DS_TYPOGRAPHY.bodyMd, fontWeight: 600, color: DS_COLORS.text.primary }}>
        {state.label}
      </span>
      {coordinate && (
        <span data-testid="ds-sector-coordinate" style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted }}>
          {coordinate}
        </span>
      )}
      {levelsMissing && (
        <span data-testid="ds-sector-levels-missing" style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted }}>
          First-level and second-level buckets not recorded on the company record
        </span>
      )}
      {state.canonical_id && (
        <span data-testid="ds-sector-id" style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted }}>
          Company id: {state.canonical_id}
        </span>
      )}
    </div>
  );
}

/**
 * Cross-sector context appendix — the firm's own CEO-accepted cross-sector patterns
 * for this startup's sector, surfaced beside the screen as background reading.
 *
 * WI-DEALSCREEN-READER-READAI-AND-KNOWLEDGE-TRUTH-001 Leg 1 — the render last mile for
 * Plan B reader #2. The backend reader
 * (`deal_screen_cross_sector_context.read_accepted_cross_sector_context`) shipped in
 * #1164 and persists onto `startups.deal_screen.cross_sector_context`, but nothing read
 * it back out to a human, so an accepted pattern could never reach the CEO.
 *
 * OBSERVE-ONLY, and the panel says so: the backend runs this read AFTER score and floor
 * are locked, so nothing here moved the verdict. Rendering it beside the verdict without
 * that sentence would imply otherwise.
 *
 * Renders nothing at all when the list is empty — an honest absence, never a
 * "no patterns found" placeholder that reads like a finding. Retired patterns are
 * excluded upstream by the reader and can never arrive here.
 */
export function CrossSectorContextBlock({ patterns }) {
  const [open, setOpen] = useState(false);
  const rows = Array.isArray(patterns) ? patterns.filter(Boolean) : [];
  if (rows.length === 0) return null;

  const labels = DEAL_CONFIG.crossSectorContextLabels || {};
  const countSuffix = rows.length === 1
    ? (labels.countSuffix || 'accepted pattern')
    : (labels.countSuffixPlural || 'accepted patterns');

  return (
    <div style={{
      background: DS_COLORS.surface.white,
      border: `1px solid ${DS_COLORS.border.subtle}`,
      borderRadius: DS_BORDERS.radiusCard,
      marginBottom: '12px',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          padding: '12px 18px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          minHeight: `${BAND_WORD_ALWAYS_VISIBLE.minTouchTargetPx}px`,
        }}
      >
        <span style={{ ...DS_TYPOGRAPHY.sectionTitle, color: DS_COLORS.text.primary }}>
          {labels.heading || 'Patterns We Have Accepted'}
        </span>
        <span style={{
          ...DS_TYPOGRAPHY.monoSub,
          fontSize: '11px',
          color: DS_COLORS.text.muted,
        }}>{rows.length} {countSuffix}</span>
        <span style={{
          marginLeft: 'auto',
          color: DS_COLORS.text.muted,
          fontSize: '12px',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 200ms ease',
        }}>▾</span>
      </button>

      {open && (
        <div style={{ padding: '0 18px 14px 18px' }}>
          <p style={{
            ...DS_TYPOGRAPHY.monoSub,
            fontSize: '13px',
            color: DS_COLORS.text.muted,
            margin: '0 0 12px 0',
          }}>{labels.observeOnlyNote || 'Background only — these did not affect this verdict.'}</p>

          {rows.map((row, i) => (
            <div
              key={row.kb_id || i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                marginBottom: i < rows.length - 1 ? '14px' : 0,
                paddingLeft: '10px',
                borderLeft: `2px solid ${DS_COLORS.border.subtle}`,
              }}
            >
              <span style={{
                ...DS_TYPOGRAPHY.bodyMd,
                fontWeight: 700,
                color: DS_COLORS.text.primary,
              }}>{row.title}</span>
              {row.canonical_statement && (
                <span style={{
                  ...DS_TYPOGRAPHY.bodyMd,
                  color: DS_COLORS.text.primary,
                }}>{row.canonical_statement}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FactGateBlock({ factGate }) {
  const [open, setOpen] = useState(false);
  if (!factGate || factGate.action === 'allow' || factGate.action == null) return null;
  const interestingClaims = (factGate.claims || []).filter(
    (c) => c.disposition === 'down_corrected' || c.disposition === 'blocked' || c.disposition === 'stripped'
  );
  if (interestingClaims.length === 0) return null;

  const actionColor = factGate.action === 'blocked'
    ? DS_COLORS.text.error
    : DS_COLORS.signal.moderate;
  const actionLabel = factGate.action === 'blocked'
    ? 'Blocked — hook claim unverifiable'
    : factGate.action === 'down_corrected'
    ? 'Figures corrected downward'
    : 'Figures stripped (unverified)';

  /**
   * Normalise a value for dedup comparison: strip whitespace, commas, and
   * trailing punctuation, then lowercase. "9" and " 9 " and "9," all match.
   */
  const normaliseValue = (v) => {
    if (v == null) return '';
    return String(v).trim().toLowerCase().replace(/,/g, '').replace(/[.;:!]+$/, '').replace(/\s+/g, ' ');
  };

  /**
   * Derive a plain-English disposition label from the raw disposition enum.
   * Never surfaces raw machine tokens in reader output.
   */
  const dispositionLabel = (d) => {
    if (d === 'down_corrected') return 'Corrected down';
    if (d === 'stripped') return 'Stripped — unverifiable';
    if (d === 'blocked') return 'Blocked — hook claim';
    return d || 'Flagged';
  };

  return (
    <div style={{
      background: DS_COLORS.surface.white,
      border: `1px solid ${actionColor}`,
      borderRadius: DS_BORDERS.radiusCard,
      marginBottom: '12px',
      overflow: 'hidden',
    }}>
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          padding: '12px 18px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          minHeight: `${BAND_WORD_ALWAYS_VISIBLE.minTouchTargetPx}px`,
        }}
      >
        <span style={{ ...DS_TYPOGRAPHY.sectionTitle, color: DS_COLORS.text.primary }}>
          Fact Check
        </span>
        <span style={{
          ...DS_TYPOGRAPHY.verdictPillText,
          fontSize: '11px',
          color: actionColor,
          background: DS_COLORS.surface.slate,
          borderRadius: DS_BORDERS.radiusPill,
          padding: '3px 10px',
          border: `1px solid ${actionColor}`,
          fontWeight: 700,
        }}>{actionLabel}</span>
        <span style={{
          ...DS_TYPOGRAPHY.monoSub,
          fontSize: '11px',
          color: DS_COLORS.text.muted,
        }}>{interestingClaims.length} item{interestingClaims.length !== 1 ? 's' : ''}</span>
        <span style={{
          marginLeft: 'auto',
          color: DS_COLORS.text.muted,
          fontSize: '12px',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 200ms ease',
        }}>▾</span>
      </button>

      {/* Appendix rows — collapsed by default */}
      {open && (
        <div style={{ padding: '0 18px 14px 18px' }}>
          {interestingClaims.map((c, i) => {
            const deckNorm = normaliseValue(c.deck_value);
            const verNorm = normaliseValue(c.verified_value);
            const hasDiff = c.deck_value && c.verified_value && deckNorm !== verNorm;

            return (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                marginBottom: i < interestingClaims.length - 1 ? '14px' : 0,
                paddingLeft: '10px',
                borderLeft: `2px solid ${actionColor}`,
              }}>
                {/* Row header: is_hook badge + disposition label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {c.is_hook && (
                    <span style={{
                      ...DS_TYPOGRAPHY.verdictPillText,
                      fontSize: '10px',
                      color: DS_COLORS.text.error,
                      background: DS_COLORS.surface.slate,
                      borderRadius: DS_BORDERS.radiusPill,
                      padding: '1px 6px',
                      border: `1px solid ${DS_COLORS.text.error}`,
                    }}>Hook claim</span>
                  )}
                  <span style={{
                    ...DS_TYPOGRAPHY.verdictPillText,
                    fontSize: '10px',
                    color: DS_COLORS.text.muted,
                    background: DS_COLORS.surface.slate,
                    borderRadius: DS_BORDERS.radiusPill,
                    padding: '1px 6px',
                    border: `1px solid ${DS_COLORS.border.default}`,
                  }}>{dispositionLabel(c.disposition)}</span>
                </div>

                {/* Claimed */}
                {c.claim && (
                  <div>
                    <span style={{ ...DS_TYPOGRAPHY.monoLabel, fontSize: '10px', color: DS_COLORS.text.muted }}>CLAIMED</span>
                    <p style={{
                      ...DS_TYPOGRAPHY.bodySm,
                      fontSize: '13px',
                      color: DS_COLORS.text.primary,
                      margin: '2px 0 0',
                      lineHeight: 1.4,
                    }}>{c.claim}</p>
                  </div>
                )}

                {/* Found — only when deck and verified values differ */}
                {hasDiff && (
                  <div>
                    <span style={{ ...DS_TYPOGRAPHY.monoLabel, fontSize: '10px', color: DS_COLORS.text.muted }}>FOUND</span>
                    <p style={{
                      ...DS_TYPOGRAPHY.monoSub,
                      fontSize: '12px',
                      color: DS_COLORS.text.secondary,
                      margin: '2px 0 0',
                    }}>
                      <span style={{ color: DS_COLORS.text.error, textDecoration: 'line-through' }}>{c.deck_value}</span>
                      {' → '}
                      <span style={{ color: DS_COLORS.text.success }}>{c.verified_value}</span>
                      {c.source_url && (
                        <a
                          href={c.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            marginLeft: '6px',
                            ...DS_TYPOGRAPHY.monoSub,
                            fontSize: '11px',
                            color: DS_COLORS.text.brand,
                            textDecoration: 'underline',
                          }}
                        >source</a>
                      )}
                    </p>
                  </div>
                )}

                {/* Why */}
                {c.reason && (
                  <div>
                    <span style={{ ...DS_TYPOGRAPHY.monoLabel, fontSize: '10px', color: DS_COLORS.text.muted }}>WHY</span>
                    <p style={{
                      ...DS_TYPOGRAPHY.bodySm,
                      fontSize: '12px',
                      color: DS_COLORS.text.secondary,
                      margin: '2px 0 0',
                      lineHeight: 1.4,
                    }}>{c.reason}</p>
                  </div>
                )}

                {/* Implication */}
                {c.implication && (
                  <div>
                    <span style={{ ...DS_TYPOGRAPHY.monoLabel, fontSize: '10px', color: DS_COLORS.text.muted }}>IMPLICATION</span>
                    <p style={{
                      ...DS_TYPOGRAPHY.bodySm,
                      fontSize: '12px',
                      color: DS_COLORS.text.primary,
                      margin: '2px 0 0',
                      lineHeight: 1.4,
                    }}>{c.implication}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// styleOverride lets the same badge chrome render a CEO decision (active/passed)
// instead of an agent call_decision — see resolveHeaderVerdict + CEO_DECISION_DISPLAY.
function CallBadgeComponent({ decision, size = 'md', styleOverride = null }) {
  const decisionStyle = styleOverride || CALL_STYLE[decision];
  const badgeKey = decisionStyle?.badgeKey;
  const badge = badgeKey ? CALL_BADGE[badgeKey] : CALL_BADGE.notWorthACall;
  const label = decisionStyle?.label || CALL_DECISION_LABELS[decision] || decision;
  const isLg = size === 'lg';

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: badge.background,
      color: badge.text,
      border: `${BADGE_SHAPE.borderWidth} solid ${badge.border}`,
      borderRadius: BADGE_SHAPE.borderRadius,
      padding: isLg ? '9px 22px' : `${BADGE_SHAPE.paddingTop} ${BADGE_SHAPE.paddingRight} ${BADGE_SHAPE.paddingBottom} ${BADGE_SHAPE.paddingLeft}`,
      fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily,
      fontSize: isLg ? '14px' : BADGE_SHAPE.fontSize,
      fontWeight: BADGE_SHAPE.fontWeight,
      letterSpacing: '0.1px',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}>
      {badge.dotAnimation === 'pulse' ? (
        <PulseDot size={isLg ? 8 : parseFloat(badge.dotSize) || 6} color={badge.dot} />
      ) : (
        <span style={{
          width: isLg ? '8px' : badge.dotSize,
          height: isLg ? '8px' : badge.dotSize,
          borderRadius: '50%',
          background: badge.dot,
          flexShrink: 0,
        }} />
      )}
      {label}
    </span>
  );
}

function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false);
  const enabled = KPI_TILE.tooltipsEnabled !== false;

  return (
    <div
      style={{ position: 'relative', display: 'flex', flex: 1, minWidth: '90px' }}
      onMouseEnter={() => enabled && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {enabled && visible && text && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: DS_COLORS.text.primary,
          color: DS_COLORS.surface.white,
          fontSize: '12px',
          fontFamily: DS_TYPOGRAPHY.monoLabel.fontFamily,
          fontWeight: DS_TYPOGRAPHY.monoLabel.fontWeight,
          lineHeight: '1.5',
          padding: '7px 11px',
          borderRadius: '6px',
          maxWidth: '220px',
          whiteSpace: 'normal',
          zIndex: 50,
          pointerEvents: 'none',
          boxShadow: DS_COLORS.shadow.tooltip,
        }}>
          {text}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: `6px solid ${DS_COLORS.text.primary}`,
          }} />
        </div>
      )}
    </div>
  );
}

function KpiStat({ label, value, sub, sub2, verdict }) {
  const accentColor = KPI_TILE.accentBar.byVerdict[verdict] || KPI_TILE.accentBar.byVerdict.unclear;
  const valueColor = KPI_TILE.valueColor[verdict] || KPI_TILE.valueColor.default;
  const tileBg = KPI_TILE.tileBg[verdict] ?? KPI_TILE.tileBg.default;

  return (
    <div style={{
      background: tileBg,
      border: KPI_TILE.shell.border,
      borderRadius: KPI_TILE.shell.borderRadius,
      padding: KPI_TILE.shell.padding,
      position: 'relative',
      overflow: 'hidden',
      flex: 1,
      minWidth: 0,
    }}>
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: KPI_TILE.accentBar.height,
        borderRadius: KPI_TILE.accentBar.borderRadius,
        background: accentColor,
      }} />
      <FieldLabel>{label}</FieldLabel>
      <div style={{
        ...DS_TYPOGRAPHY.kpiValue,
        color: valueColor,
        marginBottom: '3px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>{value}</div>
      {sub && (
        <div className="text-gray-400" style={{ ...DS_TYPOGRAPHY.monoSub, marginBottom: sub2 ? '2px' : 0 }}>{sub}</div>
      )}
      {sub2 && (
        <div className="text-gray-400" style={{ ...DS_TYPOGRAPHY.monoSub, opacity: 0.75 }}>{sub2}</div>
      )}
    </div>
  );
}

// ── Market Two-Layer Block (WI-3) ────────────────────────────────
// Renders inside the Market SectionPanel body only.
// Market Reality is context-only (top); Market Pull is the sole band-setter (below).
// NEVER drives or overrides the band — that is determined by the backend.

function MarketTwoLayerBlock({ section }) {
  const reality = section?.market_reality;
  const pull = section?.market_pull;
  if (!reality && !pull) return null;

  const subHeaderStyle = {
    ...DS_TYPOGRAPHY.monoLabel,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    color: DS_COLORS.text.muted,
    marginBottom: '6px',
    marginTop: 0,
  };
  const textStyle = {
    ...DS_TYPOGRAPHY.bodySm,
    fontSize: '15px',
    color: DS_COLORS.text.body,
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  };
  const blockStyle = {
    paddingTop: '14px',
    marginTop: '14px',
    borderTop: `1px solid ${DS_COLORS.border.default}`,
  };

  return (
    <div data-section="market-two-layer" style={{ marginTop: '16px' }}>
      {reality && (
        <div style={blockStyle}>
          <p style={subHeaderStyle}>{MARKET_LAYER_LABELS.context}</p>
          <p style={textStyle}>{reality}</p>
        </div>
      )}
      {pull && (
        <div style={{ ...blockStyle, marginTop: reality ? '12px' : '14px' }}>
          <p style={subHeaderStyle}>{MARKET_LAYER_LABELS.pull}</p>
          <p style={textStyle}>{pull}</p>
        </div>
      )}
    </div>
  );
}

// ── Section Panel ────────────────────────────────────────────────

function SectionPanel({ sectionId, section, startupId, isRewriting = false, onSectionUpdate }) {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  // Calibration UI — local state for prose-editor and which version of prose to render.
  const [proseEditorOpen, setProseEditorOpen] = useState(false);
  const [proseView, setProseView] = useState('default'); // 'default' | 'agent'
  // 'default' renders ceo_prose_override if present, agent prose otherwise.
  // 'agent' explicitly forces the agent's original prose (CEO toggled View agent version).

  const loadHistory = async () => {
    if (history) {
      setHistoryOpen(!historyOpen);
      return;
    }
    try {
      const data = await getDealScreenSectionHistory(startupId, sectionId);
      setHistory(data.versions || []);
      setHistoryOpen(true);
    } catch {
      setHistory([]);
      setHistoryOpen(true);
    }
  };

  if (!section) return null;

  const verdict = getVerdict(section);
  const borderColor = SECTION_PANEL.leftBorderByVerdict[verdict] || SECTION_PANEL.leftBorderByVerdict.unclear;
  const washColor = SECTION_PANEL.bodyWash[verdict] || SECTION_PANEL.bodyWash.unclear;
  const sectionText = sanitizeProse(section.prose || section.analysis || '');
  const sectionName = section.section_name || SECTION_LABELS[sectionId] || sectionId;

  const panelContent = (
    <div style={{
      background: SECTION_PANEL.shell.background,
      border: SECTION_PANEL.shell.borderOther,
      borderLeft: `4px solid ${borderColor}`,
      borderRadius: SECTION_PANEL.shell.borderRadius,
      overflow: 'hidden',
      boxShadow: SECTION_PANEL.shell.boxShadow,
      transition: 'box-shadow 200ms',
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: SECTION_PANEL.headerRow.padding,
          cursor: 'pointer',
          background: open ? SECTION_PANEL.headerRow.background : DS_COLORS.surface.warm,
          borderBottom: open ? SECTION_PANEL.headerRow.borderBottom : 'none',
          transition: 'background 150ms',
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              ...DS_TYPOGRAPHY.sectionTitle,
              color: DS_COLORS.text.primary,
            }}>{sectionName}</span>
            {section.version > 1 && (
              <span style={{
                ...DS_TYPOGRAPHY.monoLabel,
                fontSize: '10px',
                color: SECTION_PANEL.versionBadge.color,
                background: SECTION_PANEL.versionBadge.background,
                border: SECTION_PANEL.versionBadge.border,
                borderRadius: SECTION_PANEL.versionBadge.borderRadius,
                padding: SECTION_PANEL.versionBadge.padding,
                letterSpacing: '0.5px',
              }}>v{section.version}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <VerdictPillComponent verdict={verdict} />
            <span style={{
              color: DS_COLORS.text.muted,
              fontSize: '12px',
              display: 'inline-block',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}>{'▾'}</span>
          </div>
        </div>
        {section.headline && typeof section.headline === 'string' && section.headline.trim() !== '' && (
          <div style={{
            fontFamily: DS_TYPOGRAPHY.bodySm.fontFamily,
            fontSize: '15px',
            fontWeight: 700,
            color: DS_COLORS.text.primary,
            marginTop: DS_SPACING.base * 2,
          }}>{section.headline}</div>
        )}
      </div>

      {open && (
        <div>
          {/* Prose with verdict-colored wash. When ceo_prose_override is present
              and proseView !== 'agent', render the CEO version with a toggle link
              to view the agent's original. Per PageSpec V2 §15 TC-DS-CAL-03. */}
          {(() => {
            const ceoProse = section.ceo_prose_override;
            const showCeoProse = Boolean(ceoProse) && proseView === 'default';
            const renderedText = showCeoProse ? ceoProse : sectionText;
            return (
              <div style={{
                padding: SECTION_PANEL.bodyPadding,
                background: `linear-gradient(180deg, ${washColor} 0%, ${DS_COLORS.surface.white} 60%)`,
              }}>
                {/* WI-DS-RENDER-NAME-001: band WORD only — NO numeric score to reader.
                    band_score is internal-only; never rendered in reader surface. */}
                {section.band && (
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: `${Math.max(BAND_WORD_ALWAYS_VISIBLE.minFontSizePx, 16)}px`,
                      fontWeight: BAND_WORD_ALWAYS_VISIBLE.minFontWeight,
                      color: borderColor,
                      letterSpacing: '0.4px',
                    }}>
                      {internalBandToReaderLabel(section.band)}
                    </span>
                  </div>
                )}

                {ceoProse && !proseEditorOpen && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}>
                    <span style={{
                      ...DS_TYPOGRAPHY.microLabel,
                      color: DS_COLORS.text.brand,
                      background: DS_COLORS.surface.warm,
                      border: `1px solid ${DS_COLORS.border.brand}`,
                      borderRadius: DS_BORDERS.radiusPill,
                      padding: '4px 10px',
                    }}>{AGENT_CONFIG.deal_evaluator.calibrationFooter.refinedBadge.label}</span>
                    <button
                      type="button"
                      onClick={() => setProseView((v) => (v === 'agent' ? 'default' : 'agent'))}
                      style={{
                        ...DS_TYPOGRAPHY.sectionSubLabel,
                        color: DS_COLORS.text.secondary,
                        background: 'none',
                        border: 'none',
                        padding: '4px 6px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {proseView === 'agent'
                        ? AGENT_CONFIG.deal_evaluator.proseEditor.viewMyVersionLink
                        : AGENT_CONFIG.deal_evaluator.proseEditor.viewAgentVersionLink}
                    </button>
                  </div>
                )}

                {proseEditorOpen ? (
                  <SectionProseEditor
                    startupId={startupId}
                    sectionId={sectionId}
                    currentProse={ceoProse || sectionText}
                    agentVerdict={section.original_provisional_verdict || section.provisional_verdict}
                    ceoVerdictOverride={section.ceo_verdict_override}
                    onSaved={(updates) => {
                      onSectionUpdate?.(sectionId, updates);
                      setProseEditorOpen(false);
                      setProseView('default');
                    }}
                    onCancel={() => setProseEditorOpen(false)}
                  />
                ) : (
                  renderedText && (() => {
                    const chunks = renderedText.split('\n\n').filter((c) => c.trim());
                    return chunks.map((chunk, idx) => (
                      <p key={idx} style={{
                        ...DS_TYPOGRAPHY.bodySm,
                        color: DS_COLORS.text.body,
                        margin: 0,
                        marginBottom: idx < chunks.length - 1 ? `${DS_SPACING.paragraphSpacing}px` : 0,
                      }}>{chunk.trim()}</p>
                    ));
                  })()
                )}

                {/* V2 fields — named factors, structural evidence, carried question, open question */}
                {!proseEditorOpen && (
                  <>
                    <NamedFactorsBlock factors={section.named_factors} />
                    <StructuralEvidenceBlock evidence={section.structural_evidence} />
                    <CarriedQuestionBlock section={section} />
                    <OpenQuestionBlock openQuestion={section.open_question} />
                    {/* WI-3: Market two-layer block — Reality on top (context only), Pull below (band-setter).
                        Rendered ONLY in the market tile; no new tile created. */}
                    {sectionId === 'market' && (
                      <MarketTwoLayerBlock section={section} />
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Calibration footer — Accept / Override / Refine prose. TC-DS-CAL-01..03. */}
          {!proseEditorOpen && (
            <SectionCalibrationFooter
              startupId={startupId}
              sectionId={sectionId}
              agentVerdict={section.original_provisional_verdict || section.provisional_verdict}
              ceoVerdictOverride={section.ceo_verdict_override}
              ceoVerdictOverrideReason={section.ceo_verdict_override_reason}
              onOverrideCommitted={(updates) => onSectionUpdate?.(sectionId, updates)}
              onRefineProseClick={() => setProseEditorOpen(true)}
            />
          )}

          {/* Version history toggle */}
          {section.version > 1 && (
            <div style={{ padding: '0 24px 16px' }}>
              <button
                onClick={loadHistory}
                style={{
                  ...DS_TYPOGRAPHY.selectorBtn,
                  color: DS_COLORS.text.muted,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  minHeight: '44px',
                  textDecoration: 'underline',
                }}
              >
                {historyOpen ? 'Hide version history' : 'Show version history'}
              </button>
              {historyOpen && history && (
                <div style={{ marginTop: '12px' }}>
                  {history.map((ver, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: SECTION_PANEL.historyCard.background,
                        border: SECTION_PANEL.historyCard.border,
                        borderRadius: SECTION_PANEL.historyCard.borderRadius,
                        padding: SECTION_PANEL.historyCard.padding,
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{
                        ...DS_TYPOGRAPHY.monoSub,
                        color: DS_COLORS.text.muted,
                      }}>
                        v{ver.version || idx + 1}
                      </span>
                      {(ver.analysis || ver.prose) && (
                        <p style={{
                          ...DS_TYPOGRAPHY.bodySm,
                          fontSize: '13px',
                          lineHeight: 1.5,
                          color: DS_COLORS.text.primary,
                          margin: '8px 0 0',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {sanitizeProse(ver.prose || ver.analysis)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );

  if (isRewriting) {
    return (
      <div style={{
        filter: `blur(${AI_REWRITING_STATE.blurAmount})`,
        opacity: AI_REWRITING_STATE.dimOpacity,
        outline: `${AI_REWRITING_STATE.ringWidth} ${AI_REWRITING_STATE.ringStyle} ${AI_REWRITING_STATE.ringColor}`,
        animation: AI_REWRITING_STATE.pulseAnimation,
        transition: AI_REWRITING_STATE.transition,
        borderRadius: SECTION_PANEL.shell.borderRadius,
        pointerEvents: 'none',
      }}>
        {panelContent}
      </div>
    );
  }

  return panelContent;
}

// ── Analysis Audit Trail ─────────────────────────────────────────

function AnalysisAudit({ startupId }) {
  const [workItem, setWorkItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!startupId) return;
    let cancelled = false;
    getDealScreenWorkItem(startupId)
      .then((data) => {
        // `data.status` here is the work-item API response envelope sentinel
        // ('not_available' when no audit record exists) — NOT the deal_screen
        // subdocument's review_status field. SSOT for review state is
        // deal_screen.review_status. WI-DEAL-SCREEN-STATUS-OVERRIDE-001.
        if (!cancelled && data && data.status !== 'not_available') {
          setWorkItem(data);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [startupId]);

  if (!loaded || !workItem) return null;

  const results = workItem.sub_agent_results || [];
  if (results.length === 0) return null;

  return (
    <div style={{
      background: SECTION_PANEL.shell.background,
      border: SECTION_PANEL.shell.borderOther,
      borderRadius: SECTION_PANEL.shell.borderRadius,
      overflow: 'hidden',
      marginBottom: '12px',
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: SECTION_PANEL.headerRow.padding,
          cursor: 'pointer',
          background: SECTION_PANEL.headerRow.background,
          border: 'none',
          borderBottom: open ? SECTION_PANEL.headerRow.borderBottom : 'none',
        }}
      >
        <span style={{
          ...DS_TYPOGRAPHY.monoLabel,
          color: DS_COLORS.text.muted,
        }}>ANALYSIS AUDIT</span>
        <span style={{
          color: DS_COLORS.text.muted,
          fontSize: '12px',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 200ms ease',
        }}>{'▾'}</span>
      </button>
      {open && (
        <div style={{ padding: '16px 24px' }}>
          {results.map((r, idx) => {
            const passed = r.verification_passed;
            return (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '10px',
                padding: '4px 0',
                ...DS_TYPOGRAPHY.monoLabel,
                fontSize: '13px',
              }}>
                <span style={{
                  color: passed ? DS_COLORS.text.success : DS_COLORS.error?.color || DS_COLORS.text.errorStrong,
                  flexShrink: 0,
                  width: '16px',
                  textAlign: 'center',
                }}>{passed ? '✓' : '✗'}</span>
                <span style={{
                  color: DS_COLORS.text.primary,
                  minWidth: '160px',
                }}>{r.sub_agent}</span>
                <span style={{
                  color: DS_COLORS.text.muted,
                  flex: 1,
                }}>{r.verification_notes || ''}</span>
              </div>
            );
          })}
          {workItem.verification_summary && (
            <div style={{
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: `1px solid ${DS_COLORS.border.default}`,
              ...DS_TYPOGRAPHY.monoLabel,
              fontSize: '12px',
              color: DS_COLORS.text.muted,
            }}>
              {workItem.verification_summary}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Style Gate Notice ────────────────────────────────────────────
/**
 * StyleGateNotice — WI-DS-STYLE-GATE-UI-SURFACE-001.
 *
 * Shown when deal_screen.style_gate.status === 'blocked'. Leads with
 * a bold heading, plain-English subtext, a screen identifier line, and
 * a plain-English list of the triggered classes (never the raw taxonomy code).
 * A Re-run button (min-height 44px) calls the existing handleFullRerun handler.
 *
 * Business language only. No taxonomy codes, no words "gate"/"referee"/"block".
 * All visual tokens from STYLE_GATE_NOTICE (ui-invariants.js).
 * All copy from DEAL_CONFIG.styleGate (task-display-config.js).
 */
export function StyleGateNotice({ styleGate, identifier, onRerun, rerunning }) {
  const sgCfg = DEAL_CONFIG.styleGate || {};

  if (!styleGate || styleGate.status !== sgCfg.blockedStatus) {
    return null;
  }

  const classes = Array.isArray(styleGate.enforce_eligible_classes)
    ? styleGate.enforce_eligible_classes
    : [];

  return (
    <div
      role="alert"
      data-testid="ds-style-gate-banner"
      style={{
        background:   STYLE_GATE_NOTICE.background,
        borderLeft:   STYLE_GATE_NOTICE.borderLeft,
        border:       STYLE_GATE_NOTICE.borderOther,
        borderRadius: STYLE_GATE_NOTICE.borderRadius,
        padding:      STYLE_GATE_NOTICE.padding,
        marginBottom: '16px',
      }}
    >
      {/* Heading — ≥20px bold */}
      <p style={{
        fontFamily:  STYLE_GATE_NOTICE.headingFontFamily,
        fontSize:    STYLE_GATE_NOTICE.headingFontSize,
        fontWeight:  STYLE_GATE_NOTICE.headingFontWeight,
        color:       STYLE_GATE_NOTICE.headingColor,
        margin:      '0 0 8px 0',
        lineHeight:  1.3,
      }}>
        {sgCfg.noticeHeading}
      </p>

      {/* Subtext — ≥16px */}
      <p style={{
        fontFamily:  STYLE_GATE_NOTICE.bodyFontFamily,
        fontSize:    STYLE_GATE_NOTICE.bodyFontSize,
        fontWeight:  STYLE_GATE_NOTICE.bodyFontWeight,
        color:       STYLE_GATE_NOTICE.bodyColor,
        lineHeight:  STYLE_GATE_NOTICE.bodyLineHeight,
        margin:      '0 0 8px 0',
      }}>
        {sgCfg.noticeSubtext}
      </p>

      {/* Screen identifier */}
      <p style={{
        fontFamily:  STYLE_GATE_NOTICE.bodyFontFamily,
        fontSize:    STYLE_GATE_NOTICE.bodyFontSize,
        fontWeight:  STYLE_GATE_NOTICE.bodyFontWeight,
        color:       STYLE_GATE_NOTICE.bodyColor,
        lineHeight:  STYLE_GATE_NOTICE.bodyLineHeight,
        margin:      '0 0 12px 0',
      }}>
        {sgCfg.identifierPrefix}: {identifier}
      </p>

      {/* Issue list — one item per enforce-eligible class, plain English only */}
      {classes.length > 0 && (
        <ul style={{
          margin:      '0 0 16px 0',
          paddingLeft: '20px',
          listStyle:   'disc',
        }}>
          {classes.map((code) => (
            <li key={code} style={{
              fontFamily:  STYLE_GATE_NOTICE.bodyFontFamily,
              fontSize:    STYLE_GATE_NOTICE.bodyFontSize,
              fontWeight:  STYLE_GATE_NOTICE.bodyFontWeight,
              color:       STYLE_GATE_NOTICE.listColor,
              lineHeight:  STYLE_GATE_NOTICE.bodyLineHeight,
              marginBottom: '4px',
            }}>
              {(sgCfg.classLabels && sgCfg.classLabels[code]) || sgCfg.defaultClassLabel}
            </li>
          ))}
        </ul>
      )}

      {/* Re-run button — min-height 44px touch target */}
      <button
        type="button"
        data-testid="ds-style-gate-rerun"
        disabled={rerunning}
        onClick={onRerun}
        style={{
          display:         'inline-flex',
          alignItems:      'center',
          justifyContent:  'center',
          minHeight:       STYLE_GATE_NOTICE.buttonMinHeight,
          padding:         STYLE_GATE_NOTICE.buttonPadding,
          background:      DS_COLORS.surface.white,
          border:          `1.5px solid ${DS_DECISION_LAYER.amberAccentBar}`,
          borderRadius:    '6px',
          fontFamily:      DS_TYPOGRAPHY.actionBtn.fontFamily,
          fontSize:        DS_TYPOGRAPHY.actionBtn.fontSize,
          fontWeight:      DS_TYPOGRAPHY.actionBtn.fontWeight,
          color:           DS_DECISION_LAYER.amberAccentBar,
          cursor:          rerunning ? 'wait' : 'pointer',
          opacity:         rerunning ? 0.6 : 1,
          transition:      'all 150ms',
        }}
      >
        {rerunning ? sgCfg.rerunningLabel : sgCfg.rerunLabel}
      </button>
    </div>
  );
}

// ── Gate C Band Tile ─────────────────────────────────────────────
/**
 * GateCBandTile — one of the four band tiles in ROW 3 of the Gate C decision layer.
 * Renders: label + bandWord as TEXT + resolveTileReason one-liner + shadcn Collapsible.
 * Minimum 44px height per DS_SPACING.minTouchTarget.
 * data-testid="ds-band-tile" on every tile.
 */
function GateCBandTile({ tileId, label, band, bandWord, reason, isWeak, expandLabel, amberAccentBar }) {
  const [open, setOpen] = React.useState(false);
  const bandTextColor = isWeak ? amberAccentBar : DS_COLORS.text.success;
  const accentColor = isWeak ? amberAccentBar : DS_COLORS.text.success;

  return (
    <div
      data-testid="ds-band-tile"
      data-tile-id={tileId}
      style={{
        background: DS_COLORS.surface.white,
        border: `1px solid ${DS_COLORS.border.default}`,
        borderTop: `3px solid ${accentColor}`,
        borderRadius: DS_BORDERS.radiusCard,
        padding: '14px 16px 12px 16px',
        minHeight: DS_DECISION_LAYER.tileGrid.minTileHeight,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {/* Tile label */}
      <span style={{
        ...DS_TYPOGRAPHY.monoLabel,
        color: DS_COLORS.text.muted,
        display: 'block',
        fontSize: '11px',
      }}>{label.toUpperCase()}</span>

      {/* Band word — human-readable, never raw enum */}
      <span style={{
        fontFamily: DS_DECISION_LAYER.h1.fontFamily,
        fontSize: '16px',
        fontWeight: 700,
        color: bandTextColor,
        lineHeight: 1.2,
      }}>{bandWord}</span>

      {/* Collapsible reasoning one-liner — >=44px trigger */}
      {reason && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger
            aria-expanded={open}
            style={{
              background: 'none',
              border: 'none',
              padding: '6px 0 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              minHeight: `${DS_SPACING.minTouchTarget}px`,
              color: DS_COLORS.text.muted,
              fontFamily: DS_TYPOGRAPHY.monoLabel.fontFamily,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: DS_TYPOGRAPHY.monoLabel.letterSpacing,
              textTransform: 'uppercase',
            }}
          >
            {expandLabel || 'Reasoning'}
            <CaretDown
              weight="bold"
              size={12}
              style={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 150ms',
              }}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.body,
              margin: '6px 0 0 0',
              lineHeight: 1.5,
              fontSize: '13px',
            }}>
              {sanitizeProse(reason)}
            </p>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

/**
 * CounterCasePanel — Gate C: the counter-case, rendered BELOW the band tiles, collapsed.
 *
 * The Evamp output-quality council (B2) moved the contrarian out of the Verdict Card
 * (ROW 2), where it sat above the band tiles and pulled reader weight before the
 * evidence, into a click-to-expand "Why this call" panel below ROW 3. Default-collapsed.
 * Mirrors deal_screen_output_contract.json counter_case_render_position:
 *   below_section_tiles / counter_case_default_collapsed:true / "Why this call".
 */
function CounterCasePanel({ contrarian }) {
  const [open, setOpen] = React.useState(false);
  if (!contrarian) return null;
  const triggerLabel = DECISION_LAYER_CONFIG.counterCaseExpanderLabel || 'Why this call';
  const showFloorNote = contrarian.severity && contrarian.severity !== 'low';
  return (
    <div data-testid="ds-contrarian" style={{ marginBottom: '16px' }}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          aria-expanded={open}
          style={{
            background: 'none',
            border: 'none',
            padding: '0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            color: DS_COLORS.text.muted,
            fontFamily: DS_TYPOGRAPHY.monoLabel.fontFamily,
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: DS_TYPOGRAPHY.monoLabel.letterSpacing,
            textTransform: 'uppercase',
          }}
        >
          {triggerLabel}
          <CaretDown
            weight="bold"
            size={13}
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms',
            }}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div style={{ ...DS_DECISION_LAYER.contrarianBox, marginTop: '8px' }}>
            <span style={{
              display: 'block',
              ...DS_TYPOGRAPHY.monoLabel,
              color: DS_COLORS.text.muted,
              marginBottom: '8px',
            }}>{DECISION_LAYER_CONFIG.contrarianLabel || 'The counter-case'}</span>
            {showFloorNote && (
              <span style={{
                ...DS_TYPOGRAPHY.monoLabel,
                fontSize: '11px',
                color: DS_DECISION_LAYER.amberAccentBar,
                display: 'block',
                marginBottom: '6px',
              }}>{DECISION_LAYER_CONFIG.contrarianFloorNote || 'This is the weakest leg of the call.'}</span>
            )}
            <p style={{ ...DS_DECISION_LAYER.openingLine, margin: 0 }}>{sanitizeProse(contrarian.attack)}</p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ── Main Detail View ─────────────────────────────────────────────

/**
 * onRerun — optional callback fired when a Re-run starts. The parent
 * (DealEvaluatorAgent) uses this to re-arm its own useDealScreenStatus instance
 * (clearing terminalState) so the PipelineProgressCard reappears during the run.
 * WI-DS-PROGRESS-VISIBILITY-BUILD-001.
 */
export default function DealScreenDetailView({ startupId, onBack, onAccept, onReject, onDefer, onSwitchToFCA, fcaSummary, onRerun }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // CEO decision state — initialised from most recent entry in decisions array
  const [decision, setDecision] = useState(null);
  const [decidingValue, setDecidingValue] = useState(null);
  const [decisionDropdownOpen, setDecisionDropdownOpen] = useState(false);
  const [runningFCA, setRunningFCA] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [rerunOverlay, setRerunOverlay] = useState(false);
  // WI-DS-PROGRESS-VISIBILITY-BUILD-001: rerunNonce is bumped on every Re-run so
  // useDealScreenStatus re-arms even when startupId is unchanged. The hook's
  // overlay-clear effect (lines below) fires on the new running→complete transition.
  const [rerunNonce, setRerunNonce] = useState(0);
  const [adjustPanelOpen, setAdjustPanelOpen] = useState(false);
  const [sectionModifiers, setSectionModifiers] = useState(() =>
    Object.fromEntries(SECTION_IDS.map((id) => [id, { scrutiny: 0, research_depth: 0, india_lens: 0 }]))
  );
  const { status } = useDealScreenStatus(startupId, rerunNonce);
  const adjustButtonRef = useRef(null);

  // Inline name edit state
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef(null);

  const handleStartEditName = useCallback(() => {
    setEditNameValue(data?.startup_name || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  }, [data?.startup_name]);

  const handleCancelEditName = useCallback(() => {
    setEditingName(false);
  }, []);

  const handleSaveName = useCallback(async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === (data?.startup_name || '')) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await updateStartupName(startupId, trimmed);
      setData((prev) => prev ? { ...prev, startup_name: trimmed } : prev);
      setEditingName(false);
    } catch (err) {
      toast.error(err.message || 'Could not update the startup name');
    } finally {
      setSavingName(false);
    }
  }, [editNameValue, data?.startup_name, startupId]);

  // Calibration loop — merge a section's CEO override fields into local data.
  // Children (SectionCalibrationFooter, SectionProseEditor) call this with the
  // delta after the override-route response lands.
  const handleSectionCalibrationUpdate = useCallback((sectionId, updates) => {
    setData((prev) => {
      if (!prev || !prev.deal_screen) return prev;
      const currentSection = prev.deal_screen[sectionId] || {};
      return {
        ...prev,
        deal_screen: {
          ...prev.deal_screen,
          [sectionId]: { ...currentSection, ...updates },
        },
      };
    });
  }, []);

  // Calibration loop — merge call-outcome fields. Mirrors the section helper above.
  const handleCallOutcomeUpdate = useCallback((updates) => {
    setData((prev) => {
      if (!prev || !prev.deal_screen) return prev;
      return {
        ...prev,
        deal_screen: { ...prev.deal_screen, ...updates },
      };
    });
  }, []);

  const fetchDealScreen = useCallback(async () => {
    if (!startupId) return;
    try {
      const result = await getDealScreen(startupId);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load Deal Screen');
    } finally {
      setLoading(false);
    }
  }, [startupId]);

  useEffect(() => {
    fetchDealScreen();
  }, [fetchDealScreen]);

  useEffect(() => {
    if (status?.state === 'complete') {
      fetchDealScreen();
      setRerunning(false);
      setRerunOverlay(false);
    }
    if (status?.state === 'failed') {
      setRerunning(false);
      setRerunOverlay(false);
    }
  }, [status?.state, fetchDealScreen]);

  // Initialise decision from most recent entry in decisions array
  useEffect(() => {
    if (data?.deal_screen) {
      const decisions = data.decisions || [];
      const latest = decisions.length > 0 ? decisions[decisions.length - 1] : null;
      setDecision(latest);
    }
  }, [data]);

  const handleFullRerun = async () => {
    if (rerunning) return;
    setRerunning(true);
    // Bump nonce BEFORE overlay to re-arm the polling hook immediately. The hook
    // resets its status to null and starts fresh polling, so the overlay-clear
    // effect (status→complete/failed) fires on the new run's transition.
    // WI-DS-PROGRESS-VISIBILITY-BUILD-001: this is the critical fix.
    setRerunNonce((n) => n + 1);
    setRerunOverlay(true);
    // Notify the parent (DealEvaluatorAgent) so it can re-arm its own hook +
    // show the PipelineProgressCard. Mirrors handleRetry's terminalState clear.
    onRerun?.();
    try {
      await rerunDealScreen(startupId);
      toast.success(DEAL_CONFIG.rerunSuccessToast);
    } catch (err) {
      toast.error(err?.message || DEAL_CONFIG.rerunErrorToast);
      setRerunning(false);
      setRerunOverlay(false);
    }
  };

  // FCA trigger — fires PRE FCA pipeline
  const handleRunFCA = async () => {
    if (runningFCA) return;
    setRunningFCA(true);
    try {
      await runPreFCA(startupId);
      toast.success('First Cut Analysis started.');
      onSwitchToFCA?.();
    } catch (err) {
      toast.error(err.message || 'Could not start First Cut Analysis.');
    } finally {
      setRunningFCA(false);
    }
  };

  // CEO decision handler
  const handleRecordDecision = async (value) => {
    setDecidingValue(value);
    try {
      const result = await recordDealDecision(startupId, value);
      setDecision(result.decision);
      setDecisionDropdownOpen(false);
    } catch (err) {
      toast.error(err.message || 'Could not record decision.');
    } finally {
      setDecidingValue(null);
    }
  };

  // Per-section rerun with active knobs
  const hasActiveKnobs = Object.values(sectionModifiers).some(
    (m) => m.scrutiny > 0 || m.research_depth > 0 || m.india_lens > 0
  );

  // Per-section rewrite tracking — Set of section IDs currently being rerun
  const [rewritingSections, setRewritingSections] = useState(new Set());

  // Fix 3: rerun loading state
  const [rerunningSections, setRerunningSections] = useState(false);

  const handleRerun = async () => {
    setRerunningSections(true);
    // Track which sections are actively rewriting
    const activeSections = new Set();
    const entries = Object.entries(sectionModifiers);
    for (const [sid, mods] of entries) {
      if (mods.scrutiny > 0 || mods.research_depth > 0 || mods.india_lens > 0) {
        activeSections.add(sid);
      }
    }
    setRewritingSections(activeSections);
    try {
      for (const [sid, mods] of entries) {
        if (mods.scrutiny > 0 || mods.research_depth > 0 || mods.india_lens > 0) {
          await rerunDealScreenSection(startupId, sid, mods);
        }
      }
      toast.success('Reanalysis started');
      await fetchDealScreen();
      // Reset all knobs to zero on completion
      setSectionModifiers(
        Object.fromEntries(SECTION_IDS.map((id) => [id, { scrutiny: 0, research_depth: 0, india_lens: 0 }]))
      );
    } catch (err) {
      toast.error(err?.message || DEAL_CONFIG.rewriteErrorLabel);
      throw err;
    } finally {
      setRerunningSections(false);
      setRewritingSections(new Set());
    }
  };

  // ── Loading state
  if (loading) {
    return (
      <div style={{ padding: DV.wrapper.padding }}>
        <div role="status" aria-live="polite" style={{
          ...DS_TYPOGRAPHY.loadingText,
          color: DS_COLORS.text.muted,
        }}>
          Loading Deal Screen...
        </div>
      </div>
    );
  }

  // ── Error state
  if (error && !data) {
    return (
      <div style={{ padding: DV.wrapper.padding }}>
        <div style={{
          ...DS_TYPOGRAPHY.loadingText,
          color: DS_COLORS.error.color,
        }}>
          {error}
        </div>
      </div>
    );
  }

  // ── Pipeline generating state
  // `data.status` here is the GET /api/deal-screen/{id} response envelope's
  // pipeline sentinel ('generating' / 'complete' / 'not_found' / 'failed') —
  // NOT the deal_screen.review_status subdocument field. Review-lifecycle
  // state lives at data.deal_screen.review_status. SSOT note added per
  // WI-DEAL-SCREEN-STATUS-OVERRIDE-001.
  if (data?.status === 'generating') {
    return (
      <div style={{ padding: DV.wrapper.padding }}>
        <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PulseDot size={12} color={DS_COLORS.text.brand} />
          <span style={{
            ...DS_TYPOGRAPHY.statusText,
            color: DS_COLORS.text.primary,
          }}>
            Deal Screen in progress...
          </span>
        </div>
        {status?.current_phase_label && (
          <p style={{
            ...DS_TYPOGRAPHY.loadingText,
            color: DS_COLORS.text.muted,
            marginTop: '12px',
          }}>
            {status.current_phase_label}
            {status.progress_detail ? ` — ${status.progress_detail}` : ''}
          </p>
        )}
      </div>
    );
  }

  // ── Pipeline failed state (visible terminal error)
  // WI-DEAL-SCREEN-UPLOAD-ROBUSTNESS-001 / Priority 2 ("never silently do nothing").
  // The GET response surfaces a persisted `failed` run (e.g. a run that started but
  // never completed after a cold-start) so it renders as a clear, actionable error
  // instead of the muted "no screen yet" empty state below.
  if (data?.status === 'failed') {
    return (
      <div style={{ padding: DV.wrapper.padding }}>
        <div role="alert" style={{
          ...DS_TYPOGRAPHY.statusText,
          color: DS_COLORS.error.color,
          fontWeight: 700,
        }}>
          This analysis did not finish.
        </div>
        <p style={{
          ...DS_TYPOGRAPHY.loadingText,
          color: DS_COLORS.text.primary,
          marginTop: '12px',
        }}>
          {data?.error || 'Something went wrong before it completed. Please run it again.'}
        </p>
      </div>
    );
  }

  // ── No deal screen yet — but check if one is actively running first.
  // WI-DS-PROGRESS-VISIBILITY-BUILD-001 (Change B): when the polled hook shows
  // state=running/generating, the pipeline is in flight and there is no result
  // doc yet. Showing "No Deal Screen exists" here contradicts the progress overlay.
  // Render the in-progress state instead. When the hook re-arms on Re-run (via
  // rerunNonce), this branch covers the window between nonce-bump and the overlay
  // appearing, so the user never sees a contradiction.
  const dealScreen = data?.deal_screen;
  const hookIsRunning = status?.state === 'running' || status?.state === 'generating';
  if (!dealScreen) {
    if (hookIsRunning) {
      return (
        <div style={{ padding: DV.wrapper.padding }}>
          <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PulseDot size={12} color={DS_COLORS.text.brand} />
            <span style={{
              ...DS_TYPOGRAPHY.statusText,
              color: DS_COLORS.text.primary,
            }}>
              Deal Screen in progress...
            </span>
          </div>
          {status?.current_phase_label && (
            <p style={{
              ...DS_TYPOGRAPHY.loadingText,
              color: DS_COLORS.text.muted,
              marginTop: '12px',
            }}>
              {status.current_phase_label}
              {status.progress_detail ? ` — ${status.progress_detail}` : ''}
            </p>
          )}
        </div>
      );
    }
    return (
      <div style={{ padding: DV.wrapper.padding }}>
        <div style={{
          ...DS_TYPOGRAPHY.loadingText,
          color: DS_COLORS.text.muted,
        }}>
          No Deal Screen exists for this startup yet.
        </div>
      </div>
    );
  }

  const callDecision = dealScreen.call_decision || 'hold';
  const decisionStyle = CALL_STYLE[callDecision];
  const rationaleKey = decisionStyle?.rationaleKey || 'notWorthACall';
  const rationaleColors = DV.rationaleStrip[rationaleKey] || DV.rationaleStrip.notWorthACall;

  // KILL-LIST item 1: averaged composite block removed (scoreMap/total/maxScore/overallLabel/overallVerdict).
  // Headline tile now reads dealScreen.score (the FLOOR) via resolveOverallScore().
  // DEAL-SCREEN-V2-CARD-001.

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', maxWidth: DV.wrapper.maxWidth }}>
      {/* Token-authored AI rewrite pulse keyframes (registry authority — NOT a drift).
          Legacy inline `@keyframes spin` removed; spinner markup now uses <SpinIcon /> (Framer Motion). */}
      <style>{AI_REWRITING_STATE.pulseKeyframes}</style>

      {/* WI-DS-STYLE-GATE-UI-SURFACE-001: Style gate block notice.
          Leads when status==="blocked" so a held-back analysis is immediately visible
          before the Re-run / Export PDF action row. Returns null on clean screens. */}
      <StyleGateNotice
        styleGate={dealScreen?.style_gate}
        identifier={data?.startup_name || dealScreen?.screen_id}
        onRerun={handleFullRerun}
        rerunning={rerunning}
      />

      {/* Re-run + Export PDF — flush top-right, outside header card */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', padding: '12px 32px 0 32px' }}>
        <button
          type="button"
          disabled={rerunning}
          onClick={handleFullRerun}
          className={`${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: rerunning ? 'wait' : 'pointer',
            opacity: rerunning ? 0.6 : 1,
            transition: 'all 150ms',
          }}
        >
          {rerunning
            ? <SpinIcon size={AI_REWRITING_STATE.spinnerSize} />
            : null}
          {rerunning ? DEAL_CONFIG.rerunLoadingLabel : DEAL_CONFIG.rerunLabel}
        </button>
        <a
          href={exportDealScreenPdf(startupId)}
          download="DealScreen.pdf"
          rel="noopener noreferrer"
          className={`${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            padding: '10px 20px',
            borderRadius: '6px',
            textDecoration: 'none',
            transition: 'all 150ms',
          }}
        >
          <FilePdf weight="bold" size={14} />
          {DEAL_CONFIG.exportPdfLabel}
        </a>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, padding: DV.wrapper.padding, paddingBottom: '12px', position: 'relative' }}>
        {rerunOverlay && (
          <div style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            background: `rgba(255,255,255,${1 - AI_REWRITING_STATE.dimOpacity})`,
            backdropFilter: `blur(${AI_REWRITING_STATE.blurAmount})`,
            WebkitBackdropFilter: `blur(${AI_REWRITING_STATE.blurAmount})`,
            borderRadius: '8px',
            pointerEvents: 'all',
          }}>
            <SpinIcon
              size={AI_REWRITING_STATE.spinnerSize}
              color={AI_REWRITING_STATE.ringColor}
            />
            <span style={{
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.secondary,
              fontWeight: 600,
            }}>
              {status?.current_phase_label
                ? `${DEAL_CONFIG.rerunOverlayPhasePrefix}${status.current_phase_label}`
                : DEAL_CONFIG.rerunLoadingLabel}
            </span>
          </div>
        )}
      {/* 1. Back button */}
      {onBack && (
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            ...DS_TYPOGRAPHY.backLink,
            color: DV.backButton.color,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 0 24px 0',
            transition: 'color 150ms',
            minHeight: '44px',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = DV.backButton.colorHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = DV.backButton.color; }}
        >
          {'←'} Back to Inbox
        </button>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          GATE C — 4-ROW DECISION LAYER (above the fold)
          ROW 1: Identity (h1 + subtitle + opening line)
          ROW 2: Verdict Card (badge + why-sentence + contrarian + CEO buttons)
          ROW 3: Four Band Tiles + carry-chain fail banner
          ROW 4: One Question
          DEPTH LAYER: reader_summary + SectionPanels + FactGate + Chain + Audit
          Authority: docs/frontend-ui/UI_DESIGN_SPEC_V1.md Gate C
          ═══════════════════════════════════════════════════════════════ */}

      {/* ── ROW 1: IDENTITY ──────────────────────────────────────────── */}
      <div style={{ marginBottom: '20px' }}>
        {editingName ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: `${DS_SPACING.buttonGap}px`,
            marginBottom: `${DS_SPACING.buttonGap}px`,
          }}>
            <input
              ref={nameInputRef}
              type="text"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEditName();
              }}
              maxLength={120}
              style={{
                flex: 1,
                ...DS_DECISION_LAYER.h1,
                color: DS_COLORS.text.primary,
                border: `${DS_BORDERS.width} solid ${DS_COLORS.text.brand}`,
                borderRadius: DS_BORDERS.radiusButton,
                padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.labelToContent}px`,
                minHeight: `${DS_SPACING.minTouchTarget}px`,
                outline: 'none',
                background: DS_COLORS.surface.white,
              }}
            />
            <button
              onClick={handleSaveName}
              disabled={savingName}
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: DS_COLORS.surface.white,
                background: DS_COLORS.text.brand,
                border: `1.5px solid ${DS_COLORS.text.brand}`,
                borderRadius: DS_BORDERS.radiusButton,
                minHeight: `${DS_SPACING.minTouchTarget}px`,
                padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.cardPadding}px`,
                cursor: savingName ? 'not-allowed' : 'pointer',
                opacity: savingName ? 0.6 : 1,
              }}
            >
              {savingName ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancelEditName}
              disabled={savingName}
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: DS_COLORS.text.secondary,
                background: DS_COLORS.surface.white,
                border: `1.5px solid ${DS_COLORS.border.default}`,
                borderRadius: DS_BORDERS.radiusButton,
                minHeight: `${DS_SPACING.minTouchTarget}px`,
                padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.cardPadding}px`,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: `${DS_SPACING.buttonGap}px` }}>
            {/* Gate C: h1 with isFilenameName guard */}
            <h1
              data-testid="ds-identity-h1"
              style={{
                ...DS_DECISION_LAYER.h1,
                margin: 0,
                flex: 1,
              }}
            >
              {isFilenameName(data.startup_name, dealScreen?.filename_stem)
                ? (DECISION_LAYER_CONFIG.nameNotDetected || 'Company name not detected')
                : (data.startup_name || DECISION_LAYER_CONFIG.nameNotDetected || 'Deal Screen')}
            </h1>
            <button
              onClick={handleStartEditName}
              title={SHARED_UI_LABELS.editStartupNameTitle}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: `${DS_SPACING.minTouchTarget}px`,
                minHeight: `${DS_SPACING.minTouchTarget}px`,
                borderRadius: DS_BORDERS.radiusButton,
                flexShrink: 0,
                marginTop: '4px',
              }}
            >
              <PencilSimple size={16} color={DS_COLORS.text.muted} weight="regular" />
            </button>
          </div>
        )}

        {/* Subtitle: stage. The screen's own sector is not printed: the company's sector is the state line below (ruling 241, Law 8.4). */}
        {dealScreen.detected_stage && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
            <span style={{ ...DS_TYPOGRAPHY.monoLabel, color: DS_COLORS.text.muted }}>
              {STAGE_LABELS[dealScreen.detected_stage] || dealScreen.detected_stage}
            </span>
          </div>
        )}

        {/* Sector state: what the startup record holds, in the four ruled words (row R1) */}
        <SectorStateLine state={data?.sector_state} />

        {/* Opening line — Inter >=16px */}
        {dealScreen.opening_line && (
          <p
            data-testid="ds-opening-line"
            style={{
              ...DS_DECISION_LAYER.openingLine,
              margin: '10px 0 0 0',
            }}
          >
            {sanitizeProse(dealScreen.opening_line)}
          </p>
        )}
      </div>

      {/* ── ROW 2: VERDICT CARD ──────────────────────────────────────── */}
      {(() => {
        const weakBandSet = new Set(DECISION_LAYER_CONFIG.weakBands || ['WEAK', 'NO_SIGNAL']);
        const sectionBands = SECTION_IDS.map((id) => dealScreen[id]?.band).filter(Boolean);
        const allSectionsWeak = sectionBands.length > 0 && sectionBands.every((b) => weakBandSet.has(b));
        const chainStatus = dealScreen.director?.chain_status || dealScreen.carry_chain?.chain_status || null;
        const headerVerdict = resolveHeaderVerdict(decision, dealScreen.call_decision);

        return (
          <div
            data-testid="ds-verdict-card"
            data-chain-status={chainStatus || 'pass'}
            style={{
              background: DS_COLORS.surface.white,
              border: `1px solid ${DS_COLORS.border.default}`,
              borderRadius: DS_BORDERS.radiusCard,
              marginBottom: '16px',
              overflow: 'hidden',
              boxShadow: DV.headerCard.boxShadow,
            }}
          >
            {/* Amber accent bar — visible when ALL sections are weak/no-signal */}
            {allSectionsWeak && (
              <div
                data-testid="ds-verdict-accent-bar"
                style={{
                  height: '4px',
                  background: DS_DECISION_LAYER.amberAccentBar,
                  width: '100%',
                }}
                aria-hidden="true"
              />
            )}

            <div style={{ padding: '20px 24px' }}>
              {/* Badge row */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'wrap',
                marginBottom: '14px',
              }}>
                <div data-testid="ds-call-badge" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {headerVerdict.source === 'ceo' ? (
                    <>
                      <CallBadgeComponent
                        decision={headerVerdict.value}
                        size="lg"
                        styleOverride={CEO_DECISION_DISPLAY[headerVerdict.value] || null}
                      />
                      <span style={{ ...DS_TYPOGRAPHY.monoLabel, color: DS_COLORS.text.muted }}>
                        {`${CEO_DECISION_DISPLAY.agentNotePrefix || 'Agent rec'}: ${decisionStyle?.label || CALL_DECISION_LABELS[callDecision] || callDecision}`}
                      </span>
                    </>
                  ) : (
                    <CallBadgeComponent decision={callDecision} size="lg" />
                  )}
                </div>

                {/* CEO decision buttons — reuse existing handlers */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {decision ? (() => {
                    const isActive = decision.value === 'active';
                    const btnStyle = isActive ? INTENT_TO_BUTTON.primary : INTENT_TO_BUTTON.negative;
                    const ts = decision.timestamp ? new Date(decision.timestamp) : null;
                    const dateLabel = ts ? `${ts.toLocaleString('en-US', { month: 'short' })} ${ts.getFullYear()}` : '';
                    return (
                      <span
                        className={`${btnStyle.bg} ${btnStyle.text} ${btnStyle.border} font-heading text-sm font-semibold border-[1.5px] border-solid`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          minHeight: `${DS_SPACING.minTouchTarget}px`,
                          padding: '10px 20px',
                          borderRadius: '6px',
                        }}
                      >
                        {isActive ? 'Active' : 'Passed'}{dateLabel ? ` — ${dateLabel}` : ''}
                      </span>
                    );
                  })() : (
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setDecisionDropdownOpen((o) => !o)}
                        disabled={!!decidingValue}
                        className={`${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          minHeight: `${DS_SPACING.minTouchTarget}px`,
                          padding: '10px 20px',
                          borderRadius: '6px',
                          cursor: decidingValue ? 'wait' : 'pointer',
                          opacity: decidingValue ? 0.6 : 1,
                          transition: 'all 150ms',
                        }}
                      >
                        {decidingValue ? 'Recording…' : 'Record Decision'}
                        <CaretDown weight="bold" size={14} />
                      </button>
                      {decisionDropdownOpen && !decidingValue && (
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          right: 0,
                          background: DS_COLORS.surface.white,
                          border: `1px solid ${DS_COLORS.border.default}`,
                          borderRadius: '8px',
                          boxShadow: DS_COLORS.shadow.tooltip,
                          zIndex: 20,
                          minWidth: '160px',
                          overflow: 'hidden',
                        }}>
                          {[
                            { value: 'active', label: 'Active', intent: 'primary' },
                            { value: 'passed', label: 'Passed', intent: 'negative' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => handleRecordDecision(opt.value)}
                              className="font-heading text-sm font-semibold"
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '12px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: DS_COLORS.text.primary,
                                transition: 'background 100ms',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = DS_COLORS.surface.slate; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Why-sentence */}
              {dealScreen.call_decision_reason && (
                <p style={{
                  ...DS_TYPOGRAPHY.bodyMd,
                  color: DS_COLORS.text.primary,
                  margin: '0 0 16px 0',
                }}>
                  {sanitizeProse(dealScreen.call_decision_reason)}
                </p>
              )}
              {/* The counter-case moved OUT of the Verdict Card to a collapsed
                  "Why this call" panel below the band tiles (Evamp council, B2) —
                  see <CounterCasePanel> after ROW 3. */}
            </div>
          </div>
        );
      })()}

      {/* Call outcome strip */}
      <CallOutcomeStrip
        startupId={startupId}
        ceoCallOutcome={dealScreen.ceo_call_outcome}
        ceoCallOutcomeAt={dealScreen.ceo_call_outcome_at}
        ceoCallOutcomeNote={dealScreen.ceo_call_outcome_note}
        onOutcomeCommitted={handleCallOutcomeUpdate}
      />

      {/* ── ROW 3: FOUR BAND TILES ─────────────────────────────────────── */}
      {(() => {
        const weakBandSet = new Set(DECISION_LAYER_CONFIG.weakBands || ['WEAK', 'NO_SIGNAL']);
        const chainStatus = dealScreen.director?.chain_status || dealScreen.carry_chain?.chain_status || null;
        const overallResolved = resolveOverallScore(dealScreen);

        const tiles = [
          {
            id: 'overall',
            label: DECISION_LAYER_CONFIG.overallTileLabel || 'Overall',
            band: overallResolved.band,
            reason: null,
          },
          ...SECTION_IDS.map((id) => {
            const sec = dealScreen[id];
            return {
              id,
              label: SECTION_LABELS[id] || id,
              band: sec?.band || 'NO_SIGNAL',
              reason: resolveTileReason(sec),
            };
          }),
        ];

        return (
          <>
            {chainStatus === 'fail' && (
              <div
                data-testid="ds-carry-chain-fail-banner"
                role="alert"
                style={{
                  background: DS_DECISION_LAYER.carryChainFailBanner.background,
                  borderLeft: DS_DECISION_LAYER.carryChainFailBanner.borderLeft,
                  border: DS_DECISION_LAYER.carryChainFailBanner.borderOther,
                  borderRadius: DS_DECISION_LAYER.carryChainFailBanner.borderRadius,
                  padding: DS_DECISION_LAYER.carryChainFailBanner.padding,
                  marginBottom: '12px',
                  minHeight: DS_DECISION_LAYER.carryChainFailBanner.minHeight,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <p style={{
                  ...DS_TYPOGRAPHY.bodySm,
                  color: DS_DECISION_LAYER.carryChainFailBanner.textColor,
                  margin: 0,
                  fontWeight: 600,
                }}>
                  {DECISION_LAYER_CONFIG.carryChainFailBanner || 'The reasoning chain has a broken link — read the section detail before deciding.'}
                </p>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: DS_DECISION_LAYER.tileGrid.columns,
              gap: DS_DECISION_LAYER.tileGrid.gap,
              marginBottom: '16px',
            }}>
              {tiles.map((tile) => {
                const isWeak = weakBandSet.has(tile.band);
                const bandWord = resolveBandWord(tile.band);
                return (
                  <GateCBandTile
                    key={tile.id}
                    tileId={tile.id}
                    label={tile.label}
                    band={tile.band}
                    bandWord={bandWord}
                    reason={tile.reason}
                    isWeak={isWeak}
                    expandLabel={DECISION_LAYER_CONFIG.tileExpandLabel || 'Reasoning'}
                    amberAccentBar={DS_DECISION_LAYER.amberAccentBar}
                  />
                );
              })}
            </div>
          </>
        );
      })()}

      {/* ── COUNTER-CASE: below the band tiles, collapsed "Why this call" (Evamp council B2) ── */}
      <CounterCasePanel contrarian={resolveFloorContrarian(dealScreen)} />

      {/* ── ROW 4: ONE QUESTION ──────────────────────────────────────── */}
      {(() => {
        const oneQ = resolveOneQuestion(dealScreen);
        if (!oneQ) return null;
        return (
          <div
            data-testid="ds-one-question"
            style={{
              background: DS_DECISION_LAYER.questionBox.background,
              borderLeft: DS_DECISION_LAYER.questionBox.borderLeft,
              borderRadius: DS_DECISION_LAYER.questionBox.borderRadius,
              padding: DS_DECISION_LAYER.questionBox.padding,
              minHeight: DS_DECISION_LAYER.questionBox.minHeight,
              marginBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span style={{
              ...DS_TYPOGRAPHY.monoLabel,
              color: DS_COLORS.text.muted,
              display: 'block',
            }}>{DECISION_LAYER_CONFIG.oneQuestionLabel || 'Question for the call'}</span>
            <p style={{
              fontFamily: DS_DECISION_LAYER.questionBox.fontFamily,
              fontSize: DS_DECISION_LAYER.questionBox.fontSize,
              fontWeight: DS_DECISION_LAYER.questionBox.fontWeight,
              lineHeight: DS_DECISION_LAYER.questionBox.lineHeight,
              color: DS_DECISION_LAYER.questionBox.color,
              margin: 0,
            }}>
              {sanitizeProse(oneQ.question)}
            </p>
            {oneQ.source && (
              <span style={{
                ...DS_TYPOGRAPHY.monoSub,
                color: DS_COLORS.text.muted,
                fontSize: '12px',
              }}>
                {`${DECISION_LAYER_CONFIG.oneQuestionSourceLabel || 'From'}: ${oneQ.source}`}
              </span>
            )}
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════
          DEPTH LAYER — below the fold
          ══════════════════════════════════════════════════════════════ */}

      {dealScreen.reader_summary && (
        <div style={{
          background: DS_COLORS.surface.white,
          border: `1px solid ${DS_COLORS.border.default}`,
          borderRadius: DS_BORDERS.radiusCard,
          padding: '14px 18px',
          marginBottom: '12px',
        }}>
          <span style={{
            display: 'block',
            ...DS_TYPOGRAPHY.monoLabel,
            color: DS_COLORS.text.muted,
            marginBottom: '8px',
          }}>READER SUMMARY</span>
          <p style={{
            ...DS_TYPOGRAPHY.bodyMd,
            color: DS_COLORS.text.primary,
            margin: 0,
          }}>{sanitizeProse(dealScreen.reader_summary)}</p>
        </div>
      )}

      <CarryChainStatusBlock carryChain={dealScreen.carry_chain} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
        {SECTION_IDS.map((sectionId) => (
          <SectionPanel
            key={sectionId}
            sectionId={sectionId}
            section={dealScreen[sectionId] || null}
            startupId={startupId}
            isRewriting={rewritingSections.has(sectionId)}
            onSectionUpdate={handleSectionCalibrationUpdate}
          />
        ))}
      </div>

      {/* Fact-gate corrections appendix — collapsed, BELOW the section body
          (a subordinate appendix, not interleaved above the prose). Evamp council, B4. */}
      <FactGateBlock factGate={dealScreen.fact_gate} />

      {/* The firm's own accepted cross-sector patterns for this sector — background
          reading, read after the verdict was settled. Renders nothing when there is
          nothing accepted for the sector. */}
      <CrossSectorContextBlock patterns={dealScreen.cross_sector_context} />

      <AnalysisAudit startupId={startupId} />

      {/* Error banner */}
      {error && (
        <div style={{
          background: DS_COLORS.error.background,
          border: DS_COLORS.error.border,
          borderRadius: '8px',
          padding: '16px',
          marginTop: '16px',
        }}>
          <span style={{
            ...DS_TYPOGRAPHY.loadingText,
            color: DS_COLORS.error.color,
          }}>
            {error}
          </span>
        </div>
      )}
      </div>

      {/* Adjust Analysis button — above action bar */}
      {dealScreen && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 32px 8px 32px' }}>
          <button
            ref={adjustButtonRef}
            type="button"
            onClick={() => setAdjustPanelOpen(true)}
            className={`${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              minHeight: `${DS_SPACING.minTouchTarget}px`,
              padding: '10px 20px',
              borderRadius: DS_BORDERS.radiusButton,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
          >
            {ADJUST_BUTTON_CONFIG.label}
          </button>
        </div>
      )}

      {/* Adjust Analysis — AccrezeoSheet replaces hand-rolled position:fixed panel.
          Radix Dialog owns animation, focus trap, Escape, click-outside, scroll lock. */}
      <AccrezeoSheet
        open={adjustPanelOpen}
        onClose={() => { setAdjustPanelOpen(false); adjustButtonRef.current?.focus(); }}
        title={ADJUST_PANEL_CONFIG.title}
        ariaLabel={ADJUST_PANEL_CONFIG.title}
        width="400px"
        footer={
          <button
            type="button"
            onClick={async () => {
              try {
                await handleRerun();
                setAdjustPanelOpen(false);
                adjustButtonRef.current?.focus();
              } catch {
                // Panel stays open on error so user can retry
              }
            }}
            disabled={!hasActiveKnobs || rerunningSections || rewritingSections.size > 0}
            className={`${INTENT_TO_BUTTON.ai.bg} ${INTENT_TO_BUTTON.ai.text} ${INTENT_TO_BUTTON.ai.border} ${INTENT_TO_BUTTON.ai.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%',
              minHeight: `${DS_SPACING.minTouchTarget}px`,
              padding: '10px 20px',
              borderRadius: DS_BORDERS.radiusButton,
              cursor: (!hasActiveKnobs || rerunningSections || rewritingSections.size > 0) ? 'not-allowed' : 'pointer',
              opacity: (!hasActiveKnobs || rerunningSections || rewritingSections.size > 0) ? 0.5 : 1,
              transition: 'all 150ms',
            }}
          >
            {(rerunningSections || rewritingSections.size > 0)
              ? <><SpinIcon size={16} /> {ADJUST_PANEL_CONFIG.reanalyseButton?.loadingLabel}</>
              : ADJUST_PANEL_CONFIG.reanalyseButton?.label}
          </button>
        }
      >
        {(ADJUST_PANEL_CONFIG.sections || []).map((sec) => (
          <div key={sec.id} style={{ marginBottom: `${DS_SPACING.cardPadding}px` }}>
            <span style={{
              display: 'block',
              fontFamily: DS_TYPOGRAPHY.sectionTitle.fontFamily,
              fontSize: '14px',
              fontWeight: 600,
              color: DS_COLORS.text.primary,
              marginBottom: `${DS_SPACING.labelToContent}px`,
            }}>{sec.label}</span>
            <KnobsPanel
              values={sectionModifiers[sec.id]}
              onChange={(key, val) => setSectionModifiers((prev) => ({ ...prev, [sec.id]: { ...prev[sec.id], [key]: val } }))}
              disabled={rerunningSections || rewritingSections.size > 0}
            />
          </div>
        ))}
      </AccrezeoSheet>

      {/* 6. Action bar — sticky at bottom, DESIGN_LANGUAGE_SKILL Section 4 layout:
           AI Action left → Secondary/Danger middle → flex spacer → Primary far right */}
      {(() => {
        const allActions = DEAL_CONFIG.actions || [];
        const leftActions = allActions.filter((a) => a.position === 'left');
        const middleActions = allActions.filter((a) => a.position === 'middle');
        // 'header' position actions (e.g. export_pdf) are rendered in the page header, not here
        const rightActions = allActions.filter((a) => a.position === 'right');
        const handlerMap = { run_fca: handleRunFCA, request_revision: handleRerun, dismiss: onReject };

        // Derive FCA button state from fcaSummary prop — supports both
        // backend shape (fca_pre_call/fca_post_call) and useFCAData shape (pre/post)
        const hasPreCall = !!(fcaSummary?.fca_pre_call?.exists || fcaSummary?.pre);
        const hasPostCall = !!(fcaSummary?.fca_post_call?.exists || fcaSummary?.post);
        const fcaComplete = hasPreCall && hasPostCall;
        const renderButton = (action) => {
          const btnStyle = INTENT_TO_BUTTON[action.intent] || INTENT_TO_BUTTON.neutral;
          const handler = handlerMap[action.id];
          const isFcaBusy = action.id === 'run_fca' && runningFCA;
          if (action.id === 'run_fca' && fcaComplete) return null;
          const isRerunDisabled = action.id === 'request_revision' && !hasActiveKnobs && rewritingSections.size === 0;
          const isRerunBusy = action.id === 'request_revision' && (rerunningSections || rewritingSections.size > 0);
          const isDisabled = !handler || isFcaBusy || isRerunDisabled || isRerunBusy;
          let label = action.label;
          if (action.id === 'run_fca') {
            if (isFcaBusy) label = 'Analysing\u2026';
            else if (hasPreCall && !hasPostCall) label = DEAL_CONFIG.rerunWithTranscriptLabel || 'Re-run with Transcript';
            else label = DEAL_CONFIG.runFcaLabel || 'Run First Cut Analysis';
          }
          // Fix 3: rerun loading label
          if (action.id === 'request_revision' && (rerunningSections || rewritingSections.size > 0)) {
            label = DEAL_CONFIG.rewritingSectionLabel;
          }
          return (
            <button
              key={action.id}
              onClick={handler ? () => handler() : undefined}
              disabled={isDisabled}
              className={`${btnStyle.bg} ${btnStyle.text} ${btnStyle.border} ${btnStyle.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: `${DS_SPACING.minTouchTarget}px`,
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: isFcaBusy || isRerunBusy ? 'wait' : isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                transition: 'all 150ms',
              }}
            >
              {ACTION_ICON_MAP[action.id] && React.createElement(ACTION_ICON_MAP[action.id], { weight: 'bold', size: 16 })}
              {label}
            </button>
          );
        };

        return (
          <div style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            padding: '14px 32px',
            background: DS_COLORS.surface.white,
            borderTop: `1px solid ${DS_COLORS.border.default}`,
            boxShadow: DS_COLORS.shadow.actionBar,
            gap: '10px',
          }}>
            {/* AI Action — left */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {leftActions.map(renderButton)}
            </div>
            {/* Secondary / Danger — middle */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {middleActions.map(renderButton)}
            </div>
            {/* Flex spacer */}
            <div style={{ flex: 1 }} />
            {/* Gate C: CEO decision buttons moved to ROW 2 verdict card.
                Action bar right side retains FCA completion status only. */}
            {fcaComplete && (
              <span
                className="font-heading text-sm font-semibold text-green-600"
                style={{ padding: '10px 0' }}
              >
                Analysis complete
              </span>
            )}
          </div>
        );
      })()}
    </div>
  );
}
